<template>
  <!-- 开发运行地址：http://localhost:8094/doucan/#/body_check/AiExercisePadPlan?isApp=true&phone=13610164743&planId=1493 -->
  <!-- 测试模式地址：http://localhost:8094/doucan/#/body_check/AiExercisePadPlan?isApp=true&phone=13610164743&planId=1493&testMode=true -->
  <div class="ai-exercise-layout">
    <!-- 总运动时长悬浮显示 -->
    <div v-if="totalExerciseTime" :class="[
      'total-exercise-time-float',
      { 'countdown-critical': isCountdownCritical },
      { 'countdown-urgent': isCountdownUrgent }
    ]">
      <div class="time-float-icon">⏱️</div>
      <div class="time-float-content">
        <div class="time-float-label">
          剩余总时长
          <span v-if="isTestMode" class="test-mode-badge">测试</span>
        </div>
        <div class="time-float-value">{{ totalExerciseTime }}</div>
        <div v-if="currentVideoProgressText" class="time-float-progress" style="font-size: 16px; font-weight: bold">
          当前动作: {{ currentVideoProgressText }}
        </div>
      </div>
    </div>

    <!-- 左侧主内容区：训练视频 + 视频列表 -->
    <div class="main-content">
      <!-- 训练视频播放器区域（16:9自适应） -->
      <div class="video-player-wrapper">
        <video v-if="playingCourse && playingCourse.videoUrl" ref="mainVideo" :src="playingCourse.videoUrl"
          :poster="playingCourse.coverUrl" controls playsinline class="main-video" @ended="onVideoEnded"
          @timeupdate="onVideoTimeUpdate" @loadedmetadata="onVideoLoadedMetadata" @pause="onVideoPause"
          @play="onVideoPlay">
          您的浏览器不支持 video 标签。
        </video>
        <div v-else class="video-player-placeholder">
          <span>训练视频播放器区域</span>
        </div>
      </div>
      <!-- 视频列表区域 -->
      <div class="video-list-area">
        <div v-if="loadingCourse" class="video-list-placeholder">
          视频列表加载中...
        </div>
        <div v-else-if="courses.length === 0" class="video-list-placeholder">
          暂无训练视频
        </div>
        <div v-else class="video-playlist">
          <div v-for="(video, index) in courses" :key="index" :ref="'videoItem-' + index" class="video-item">
            <div :class="[
              'video-card',
              playingCourse && playingCourse.courseId === video.courseId
                ? 'active'
                : ''
            ]" @click="handleCourseClick(video, index)">
              <div class="video-title">
                {{ index + 1 + '. ' + video.title }}
              </div>
              <div class="video-info-right">
                <div class="video-duration">
                  {{ formatDuration(video.duration) }}
                </div>
                <div class="video-status">
                  <button v-show="playingCourse && playingCourse.courseId === video.courseId
                    " class="training-button">
                    训练中
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 右侧辅助区：摄像头、骨骼渲染、方案描述、运动统计 -->
    <div class="side-panel">
      <!-- 结束运动按钮 -->
      <div class="end-exercise-section">
        <button class="end-exercise-btn" @click="showEndConfirm">
          <span class="btn-icon">🏁</span>
          <span class="btn-text">结束运动</span>
        </button>
      </div>
      <!-- 摄像头画面 + 骨骼渲染叠加 -->
      <div class="camera-area card-area camera-overlay-container">
        <video ref="video" :style="{
          visibility:
            isLoading || errorMessage || needPermission ? 'hidden' : 'visible'
        }" autoplay class="ai-exercise__video camera-video" muted playsinline></video>
        <canvas ref="canvas" :style="{
          visibility:
            isLoading || errorMessage || needPermission ? 'hidden' : 'visible'
        }" class="ai-exercise__canvas camera-canvas"></canvas>
      </div>
      <!-- 训练方案描述说明 -->
      <div class="plan-desc-area card-area">
        <div v-if="!playingCourse || !data" class="plan-desc-placeholder">
          训练方案描述说明
        </div>
        <div v-else class="plan-desc-content">
          <span class="requirement-label" style="
              background-color: #38b48b;
              color: white;
              padding: 4px 8px;
              border-radius: 4px;
              margin-right: 8px;
            ">训练要求</span>
          <span class="plan-desc-title">{{ playingCourse.title }}</span>
          <span class="requirement-text" style="display: block">{{
            getActionRequirement(playingCourse.courseId)
            }}</span>
        </div>
      </div>
      <!-- 运动统计数据 -->
      <div class="stats-area card-area">
        <div v-show="!isLoading && !errorMessage && !needPermission" class="ai-exercise__stats">
          <div class="stats-header">
            <h3 class="stats-title">运动统计</h3>
            <div class="stats-indicator">
              <span class="indicator-dot" :class="{ completed: isAllCompleted }"></span>
              <span class="indicator-text">{{
                isAllCompleted ? '训练完成' : '实时监测中'
                }}</span>
            </div>
          </div>
          <div class="stats-grid">
            <div class="stats-card time-stats">
              <div class="stats-card-header">
                <div class="stats-icon time-icon">⏱️</div>
                <span class="stats-label">训练时长</span>
              </div>
              <div class="stats-value-section">
                <div class="stats-value">{{ formattedDuration }}</div>
              </div>
              <div class="stats-progress">
                <div class="progress-bar time-progress"></div>
              </div>
            </div>
            <div class="stats-card course-stats">
              <div class="stats-card-header">
                <div class="stats-icon course-icon">🎯</div>
                <span class="stats-label">动作进度</span>
              </div>
              <div class="stats-value-section">
                <div class="stats-value">{{ completedCount }}</div>
                <div class="stats-separator">/</div>
                <div class="stats-total">{{ totalCount }}</div>
              </div>
              <!-- 调试信息：仅开发环境显示 -->
              <!-- <div style="font-size: 10px; color: #666; margin-top: 2px;">
                已完成: {{ completedCourses.join(',') }}
              </div> -->
              <div class="stats-progress">
                <div class="progress-bar course-progress"></div>
              </div>
            </div>
            <div class="stats-card arm-stats">
              <div class="stats-card-header">
                <div class="stats-icon arm-icon">💪</div>
                <span class="stats-label">手臂运动</span>
              </div>
              <div class="stats-value-section">
                <div class="stats-value">{{ armCount }}</div>
                <div class="stats-unit">次</div>
              </div>
              <div class="stats-progress">
                <div class="progress-bar arm-progress"></div>
              </div>
            </div>
            <div class="stats-card leg-stats">
              <div class="stats-card-header">
                <div class="stats-icon leg-icon">🦵</div>
                <span class="stats-label">腿部运动</span>
              </div>
              <div class="stats-value-section">
                <div class="stats-value">{{ legCount }}</div>
                <div class="stats-unit">次</div>
              </div>
              <div class="stats-progress">
                <div class="progress-bar leg-progress"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 全局悬浮提示：加载、错误、权限 -->
    <div v-show="isLoading" class="ai-exercise__loading">
      <p>{{ loadingMessage }}</p>
    </div>
    <div v-show="errorMessage" class="ai-exercise__error">
      <p>{{ errorMessage }}</p>
      <button class="retry-button" @click="retryInitialization">重试</button>
    </div>
    <div v-show="needPermission" class="ai-exercise__permission">
      <p>此功能需要访问您的摄像头</p>
      <p class="permission-desc">
        我们将使用摄像头来识别您的运动姿势，帮助您进行运动计数
      </p>
      <button class="permission-button" @click="requestPermission">
        允许使用摄像头
      </button>
    </div>

    <!-- 结束运动确认弹框 -->
    <div v-show="showEndConfirmDialog" class="confirm-overlay">
      <div class="confirm-dialog">
        <h3 class="confirm-title">确认结束运动</h3>
        <p class="confirm-text">确定要结束本次运动训练吗？</p>
        <div class="confirm-buttons">
          <button class="confirm-btn cancel-btn" @click="cancelEndExercise">
            取消
          </button>
          <button class="confirm-btn confirm-btn-primary" @click="confirmEndExercise">
            确认结束
          </button>
        </div>
      </div>
    </div>

    <!-- 运动结束统计弹框 -->
    <div v-show="showEndStatsDialog" class="end-stats-overlay">
      <div class="end-stats-dialog">
        <!-- 头部标题区域 -->

        <!-- 左右布局主体内容 -->
        <div class="end-stats-layout">
          <!-- 左侧：数字人视频区域 -->
          <div class="end-stats-video-section">
            <div class="end-stats-video-container">
              <video v-if="showEndStatsDialog" ref="endStatsVideo" class="end-stats-digital-human-video" :src="endStatsVideoUrl"
                :muted="isEndStatsVideoMuted" playsinline
                @loadedmetadata="onEndStatsVideoLoaded"
                @error="onEndStatsVideoError" @ended="onEndStatsVideoEnded">
                您的浏览器不支持视频播放
              </video>

              <!-- 视频控制按钮 -->
              <div class="end-stats-video-controls">
                <button class="end-stats-audio-control-btn" @click="toggleEndStatsVideoMute"
                  :title="isEndStatsVideoMuted ? '开启声音' : '关闭声音'">
                  <span class="audio-icon">{{
                    isEndStatsVideoMuted ? '🔇' : '🔊'
                    }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- 右侧：统计数据区域 -->
          <div class="end-stats-data-section">
            <div class="stats-header-section">
          <div class="celebration-icon">🎉</div>
          <h2 class="end-stats-title">运动完成！</h2>
          <p class="encouragement-text">{{ encouragementText }}</p>
        </div>
            <div class="end-stats-content">
              <div class="stats-summary">
                <div class="summary-card">
                  <div class="summary-icon time-summary-icon">⏱️</div>
                  <div class="summary-data">
                    <div class="summary-value">{{ formattedDuration }}</div>
                    <div class="summary-label">训练时长</div>
                  </div>
                </div>

                <div class="summary-card">
                  <div class="summary-icon course-summary-icon">🎯</div>
                  <div class="summary-data">
                    <div class="summary-value">
                      {{ completedCount }}/{{ totalCount }}
                    </div>
                    <div class="summary-label">完成动作</div>
                  </div>
                </div>

                <div class="summary-card">
                  <div class="summary-icon arm-summary-icon">💪</div>
                  <div class="summary-data">
                    <div class="summary-value">{{ armCount }}</div>
                    <div class="summary-label">手臂运动</div>
                  </div>
                </div>

                <div class="summary-card">
                  <div class="summary-icon leg-summary-icon">🦵</div>
                  <div class="summary-data">
                    <div class="summary-value">{{ legCount }}</div>
                    <div class="summary-label">腿部运动</div>
                  </div>
                </div>
              </div>

              <div class="performance-evaluation">
                <div class="evaluation-card" style="text-align: center;">
                  <h4 class="evaluation-title">运动评价</h4>
                  <div class="evaluation-content">
                    <div class="rating-stars">
                      <span v-for="star in 5" :key="star" class="star"
                        :class="{ active: star <= exerciseRating }">⭐</span>
                    </div>
                    <p class="rating-text">{{ getRatingText() }}</p>
                    <p class="rating-text browser-back-tip">
                      💡
                      提示：训练结束后，点击浏览器【返回】按钮或左上角【返回】即可退出
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 恢复训练确认弹框 -->
    <div v-show="showRecoveryDialog" class="recovery-overlay">
      <div class="recovery-dialog">
        <div class="recovery-header">
          <div class="recovery-icon">🔄</div>
          <h3 class="recovery-title">发现中断的训练</h3>
        </div>

        <div class="recovery-content">
          <p class="recovery-text">检测到您有未完成的训练记录：</p>
          <div v-if="recoveryTrainingData" class="recovery-details">
            <div class="recovery-detail-item">
              <span class="detail-label">保存时间：</span>
              <span class="detail-value">{{
                recoveryTrainingData.saveTimeFormatted
                }}</span>
            </div>
            <div class="recovery-detail-item">
              <span class="detail-label">训练时长：</span>
              <span class="detail-value">{{
                formatDuration(recoveryTrainingData.trainingDuration * 1000)
                }}</span>
            </div>
            <div class="recovery-detail-item">
              <span class="detail-label">完成进度：</span>
              <span class="detail-value">{{ recoveryTrainingData.completedCourses.length }}/{{
                recoveryTrainingData.totalCourses
              }}
                个动作</span>
            </div>
            <div class="recovery-detail-item">
              <span class="detail-label">运动次数：</span>
              <span class="detail-value">手臂{{ recoveryTrainingData.armCount }}次，腿部{{
                recoveryTrainingData.legCount
              }}次</span>
            </div>
          </div>
          <p class="recovery-question">是否要继续上次的训练？</p>
        </div>

        <div class="recovery-buttons">
          <button class="recovery-btn cancel-recovery-btn" @click="cancelRecoveryTraining">
            重新开始
          </button>
          <button class="recovery-btn confirm-recovery-btn" @click="confirmRecoveryTraining">
            继续训练
          </button>
        </div>
      </div>
    </div>

    <!-- 开始前准备弹窗 -->
    <div v-show="showPrepareDialog" class="prepare-overlay">
      <div class="prepare-dialog">
        <div class="prepare-content">
          <!-- 左右布局：左侧视频，右侧清单和播报内容 -->
          <div class="prepare-layout">
            <!-- 左侧：数字人视频播放区 -->
            <div class="video-section">
              <div class="video-container">
                <video v-if="showPrepareDialog" ref="digitalHumanVideo" class="digital-human-video" :src="digitalHumanVideoUrl"
                  :muted="isDigitalHumanVideoMuted" playsinline>
                  您的浏览器不支持视频播放
                </video>

                <!-- 播放倒计时提示 -->
                <div v-if="showCountdown" class="play-countdown">
                  <div class="countdown-icon">🎬</div>
                  <div class="countdown-text">视频即将播放</div>
                  <div class="countdown-number">{{ countdownSeconds }}</div>
                </div>
              </div>
            </div>

            <!-- 右侧：播报内容 -->
            <div class="content-section">
              <!-- 数字人播报内容文字显示 -->
              <div class="broadcast-text-section">
                <div class="section-header">
                  <h4 class="section-title">🎯 教练指导</h4>
                </div>
                <div class="broadcast-text" :class="{ highlight: isCurrentBroadcastItem(index) }"
                  v-for="(item, index) in broadcastItems" :key="index">
                  <span class="broadcast-icon">{{ item.icon }}</span>
                  <span class="broadcast-content">{{ item.text }}</span>
                </div>
              </div>

              <!-- 开始运动按钮移到教练指导下方 -->
              <div class="prepare-action-section">
                <button class="prepare-btn start-exercise-btn" @click="startExerciseFromPrepare"
                  :class="{ ready: digitalHumanVideoCompleted }">
                  <span class="btn-icon">🚀</span>
                  <span class="btn-text">{{
                    digitalHumanVideoCompleted ? '开始运动' : '请先观看完整指导'
                    }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 休息倒计时弹窗 - 左右布局 -->
    <div v-show="showRestDialog" class="rest-overlay">
      <div class="rest-dialog">
        <!-- 左侧：数字人视频区域 -->
        <div class="rest-video-section">
          <div class="rest-video-container">
            <video v-if="showRestDialog" ref="restVideo" class="rest-digital-human-video" :src="restVideoUrl"
              :muted="isRestVideoMuted" playsinline
              @loadedmetadata="onRestVideoLoaded"
              @error="onRestVideoError" @ended="onRestVideoEnded">
              您的浏览器不支持视频播放
            </video>

            <!-- 视频控制按钮 -->
            <div class="rest-video-controls">
              <button class="rest-audio-control-btn" @click="toggleRestVideoMute"
                :title="isRestVideoMuted ? '开启声音' : '关闭声音'">
                <span class="audio-icon">{{
                  isRestVideoMuted ? '🔇' : '🔊'
                  }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- 右侧：动作完成信息和休息倒计时 -->
        <div class="rest-content-section">
          <!-- 动作完成信息 -->
          <div class="rest-header">
            <div class="rest-icon">🎉</div>
            <h3 class="rest-title">动作完成！</h3>
          </div>

          <div class="completion-info">
            <p class="rest-completion-text">恭喜！您已完成了</p>
            <div class="current-action-badge">
              <strong>{{ currentActionName }}</strong>
            </div>
          </div>

          <div class="next-action-info">
            <div class="next-action-label">下一个动作</div>
            <div class="next-action-name">{{ nextActionName }}</div>
          </div>

          <!-- 休息倒计时 -->
          <div class="rest-countdown-section">
            <div class="countdown-circle">
              <div class="countdown-number">{{ restCountdown }}</div>
              <div class="countdown-unit">秒</div>
            </div>
            <p class="rest-countdown-text">休息时间，为下一个动作做准备</p>
          </div>

          <!-- 操作按钮 -->
          <div class="rest-buttons">
            <button class="rest-btn skip-rest-btn" @click="skipRest">
              <span class="btn-icon">⏭️</span>
              <span class="btn-text">跳过休息</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 同一动作循环间隔休息弹窗 -->
    <div v-show="showLoopRestDialog" class="rest-overlay">
      <div class="rest-dialog">
        <!-- 左侧：数字人视频区域 -->
        <div class="rest-video-section">
          <div class="rest-video-container">
            <video v-if="showLoopRestDialog" ref="loopRestVideo" class="rest-digital-human-video" :src="loopRestVideoUrl"
              :muted="isLoopRestVideoMuted" playsinline
              @loadedmetadata="onLoopRestVideoLoaded"
              @error="onLoopRestVideoError" @ended="onLoopRestVideoEnded">
              您的浏览器不支持视频播放
            </video>

            <!-- 视频控制按钮 -->
            <div class="rest-video-controls">
              <button class="rest-audio-control-btn" @click="toggleLoopRestVideoMute"
                :title="isLoopRestVideoMuted ? '开启声音' : '关闭声音'">
                <span class="audio-icon">{{
                  isLoopRestVideoMuted ? '🔇' : '🔊'
                  }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- 右侧：循环间隔信息和休息倒计时 -->
        <div class="rest-content-section">
          <!-- 循环间隔信息 -->
          <div class="rest-header">
            <div class="rest-icon">⏱️</div>
            <h3 class="rest-title">动作间隔休息</h3>
          </div>

          <div class="completion-info">
            <div class="current-action-badge">
              <strong>{{ playingCourse?.title || '当前动作' }}</strong>
            </div>
          </div>

          <div class="loop-progress-info">
            <div class="progress-label">当前进度</div>
            <div class="progress-value">{{ currentVideoProgressText }}</div>
          </div>

          <!-- 休息倒计时 -->
          <div class="rest-countdown-section">
            <div class="countdown-circle">
              <div class="countdown-number">{{ loopRestCountdown }}</div>
              <div class="countdown-unit">秒</div>
            </div>
            <p class="rest-countdown-text">短暂休息，准备继续练习</p>
          </div>

          <!-- 操作按钮 -->
          <div class="rest-buttons">
            <button class="rest-btn skip-rest-btn" @click="skipLoopRest">
              <span class="btn-icon">⏭️</span>
              <span class="btn-text">跳过休息</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import BaseTitleBarScrollPage from '@/h5/doucan/components/BaseTitleBarScrollPage.vue'
import * as tf from '@tensorflow/tfjs-core'
import '@tensorflow/tfjs-backend-webgl'
import * as poseDetection from '@tensorflow-models/pose-detection'
// 新增：引入 PlanDao、DateUtils 和 DakaDao
import PlanDao from '@/h5/doucan/network/PlanDao'
import DateUtils from '@/utils/DateUtils'
import DakaDao from '@/h5/doucan/network/DakaDao'

// 添加mediaDevices polyfill
const initMediaDevices = () => {
  if (navigator.mediaDevices === undefined) {
    navigator.mediaDevices = {}
  }

  if (navigator.mediaDevices.getUserMedia === undefined) {
    navigator.mediaDevices.getUserMedia = function (constraints) {
      const getUserMedia =
        navigator.webkitGetUserMedia ||
        navigator.mozGetUserMedia ||
        navigator.msGetUserMedia

      if (!getUserMedia) {
        return Promise.reject(
          new Error('getUserMedia is not implemented in this browser')
        )
      }

      return new Promise((resolve, reject) => {
        getUserMedia.call(navigator, constraints, resolve, reject)
      })
    }
  }
}

export default {
  name: 'AiExercisePad',
  // components: { BaseTitleBarScrollPage },
  data () {
    return {
      isApp: false,
      showPre: true,
      phone: '',
      planId: '',
      detector: null,
      isModelLoaded: false,
      armCount: 0,
      legCount: 0,
      lastKeypoints: null,
      movementThreshold: 30,
      // 新增：浏览器返回控制相关
      isTrainingActive: false, // 训练是否处于活跃状态
      hasAddedUnloadListener: false, // 是否已添加卸载监听器
      // 新增：简化检测模式（解决检测过严问题）
      useSimpleDetection: true, // 暂时启用简化检测模式
      errorMessage: '',
      isLoading: true,
      loadingMessage: '正在加载模型和初始化摄像头...',
      needPermission: true,
      // 新增：动作统计优化相关变量（调整参数，提高敏感度）
      armMovementState: {
        left: {
          isMoving: false,
          lastDirection: null, // 'up', 'down', 'left', 'right'
          lastCountTime: 0,
          movementStartTime: 0,
          minMovementDuration: 150, // 降低最小动作持续时间（毫秒）
          cooldownDuration: 300, // 降低动作冷却时间（毫秒）
          movementPeaks: [], // 记录运动峰值点
          lastPosition: null
        },
        right: {
          isMoving: false,
          lastDirection: null,
          lastCountTime: 0,
          movementStartTime: 0,
          minMovementDuration: 150,
          cooldownDuration: 300,
          movementPeaks: [],
          lastPosition: null
        }
      },
      legMovementState: {
        left: {
          isMoving: false,
          lastDirection: null,
          lastCountTime: 0,
          movementStartTime: 0,
          minMovementDuration: 150,
          cooldownDuration: 300,
          movementPeaks: [],
          lastPosition: null
        },
        right: {
          isMoving: false,
          lastDirection: null,
          lastCountTime: 0,
          movementStartTime: 0,
          minMovementDuration: 150,
          cooldownDuration: 300,
          movementPeaks: [],
          lastPosition: null
        }
      },
      // 检测配置
      detectorConfig: {
        modelType: poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING,
        enableSmoothing: true,
        minPoseScore: 0.3,
        modelUrl: 'https://cdn.keihong.tech/前端库/model.json'
      },
      // 新增：人物跟踪相关
      lastSelectedPersonCenter: null, // 上一帧选中人物的中心点
      personTrackingThreshold: 150, // 人物跟踪阈值（像素）
      noPersonFrameCount: 0, // 连续无人检测的帧数
      maxNoPersonFrames: 30, // 最大允许无人检测帧数（约1秒）
      // 新增：调试模式
      debugMode: true, // 开启调试模式，显示人物中心点等信息
      // 摄像头支持状态
      isCameraSupported: false,
      // 新增：方案和课程相关
      data: null, // 方案详情
      courses: [], // 课程详细信息
      playingCourse: null, // 当前播放课程
      loadingCourse: true, // 课程加载中
      // 新增：训练统计
      trainingStartTime: null, // 训练开始时间
      trainingDuration: 0, // 训练时长（秒）
      countdownRemaining: 0, // 倒计时剩余时间（秒）
      isAutoEnding: false, // 是否正在自动结束（防止重复调用）
      isTestMode: false, // 测试模式：true时总运动时长为10秒
      isTimerPaused: false, // 计时器是否被暂停
      pausedTime: 0, // 暂停时的累计时长（秒）
      currentVideoLoopCount: 0, // 当前视频循环播放次数
      completedCourses: [], // 已完成的课程ID数组（改用数组以确保响应式）
      durationTimer: null, // 计时器
      animationFrameId: null, // 用于存储 requestAnimationFrame 的 ID
      // 视频播放状态跟踪
      currentVideoInfo: {
        courseId: null,
        duration: 0,
        currentTime: 0,
        hasBeenMarkedAsCompleted: false,
        playStartTime: null // 当前播放开始时间
      },
      // 新增：视频播放统计数据
      videoPlayStats: {}, // 格式: { courseId: { playCount: 0, totalPlayTime: 0, title: '', sessions: [] } }
      // 结束运动相关
      showEndConfirmDialog: false,
      showEndStatsDialog: false,
      // 准备和休息弹窗相关
      showPrepareDialog: false, // 是否显示开始前准备弹窗
      showRestDialog: false, // 是否显示休息倒计时弹窗
      restCountdown: 30, // 休息倒计时（秒）
      restTimer: null, // 休息计时器
      currentActionName: '', // 当前动作名称（用于休息弹窗显示）
      nextActionName: '', // 下一个动作名称
      encouragementText: '',
      exerciseRating: 0,
      // 最终的训练记录JSON数据
      finalTrainingRecordJson: null,
      // 训练数据恢复相关
      showRecoveryDialog: false,
      recoveryTrainingData: null,
      // localStorage存储key
      TRAINING_STORAGE_KEY: 'ai_exercise_training_data',
      // 自动保存相关
      autoSaveTimer: null,
      autoSaveInterval: 10000, // 10秒自动保存一次
      lastSaveTime: 0,
      // 鼓励词库
      encouragementTexts: {
        excellent: [
          '太棒了！你的毅力让人佩服！',
          '完美的运动表现，继续保持！',
          '出色的训练，你是最棒的！',
          '运动达人就是你！'
        ],
        good: [
          '很好的运动表现！',
          '坚持就是胜利！',
          '你已经超越了昨天的自己！',
          '努力的汗水不会白流！'
        ],
        normal: [
          '不错的开始，继续加油！',
          '每一次运动都是进步！',
          '运动是最好的投资！',
          '保持运动习惯，健康每一天！'
        ],
        beginner: [
          '第一步最重要，你已经迈出了！',
          '运动的路上，你我同行！',
          '每一分努力都值得鼓励！',
          '坚持下去，未来可期！'
        ]
      },
      // 数字人视频相关数据
      digitalHumanVideoUrl:
        'https://cdn.keihong.tech/img/20250703203019463.mp4', // 数字人视频地址（准备阶段）
      endStatsVideoUrl: 'https://cdn.keihong.tech/img/20250703223756637.mp4', // 结束统计数字人视频地址
      isDigitalHumanVideoPlaying: false, // 数字人视频是否正在播放
      isDigitalHumanVideoMuted: false, // 数字人视频是否静音（默认开启声音，自动播放失败时再静音）
      digitalHumanVideoProgress: 0, // 数字人视频播放进度（百分比）
      digitalHumanVideoProgressText: '00:00 / 00:00', // 视频进度文字
      digitalHumanVideoCompleted: true, // 数字人视频是否播放完成
      digitalHumanVideoPlayCount: 0, // 数字人视频播放次数计数
      digitalHumanVideoMaxPlayCount: 1, // 数字人视频最大播放次数
      currentBroadcastIndex: 0, // 当前播报的项目索引
      // 结束统计视频相关数据
      isEndStatsVideoMuted: false, // 结束统计视频是否静音（默认开启声音）
      endStatsVideoPlayCount: 0, // 结束统计视频播放次数
      endStatsVideoMaxPlayCount: 1, // 结束统计视频最大播放次数
      // 休息时间数字人视频相关数据
      restVideoUrl: 'https://cdn.keihong.tech/img/20250704004033113.mp4', // 休息时间数字人视频地址
      isRestVideoMuted: false, // 休息视频是否静音（默认开启声音）
      restVideoPlayCount: 0, // 休息视频播放次数
      restVideoMaxPlayCount: 1, // 休息视频最大播放次数
      // 同一动作循环间隔休息相关数据
      showLoopRestDialog: false, // 是否显示循环间隔休息弹窗
      loopRestCountdown: 15, // 循环间隔休息倒计时（秒）
      loopRestTimer: null, // 循环间隔休息计时器
      loopRestVideoUrl: 'https://cdn.keihong.tech/img/20250704004811082.mp4', // 循环间隔休息数字人视频地址
      isLoopRestVideoMuted: false, // 循环间隔休息视频是否静音
      loopRestVideoPlayCount: 0, // 循环间隔休息视频播放次数
      loopRestVideoMaxPlayCount: 1, // 循环间隔休息视频最大播放次数
      // 数字人视频重播定时器
      digitalHumanVideoReplayTimer: null, // 数字人视频重播定时器
      // 倒计时相关
      showCountdown: false, // 是否显示倒计时
      countdownSeconds: 1, // 倒计时秒数
      countdownTimer: null, // 倒计时定时器
      // 数字人播报内容
      broadcastItems: [
        {
          icon: '👕',
          text: '请穿着舒适的运动服装，确保身体活动自如',
          timeRange: [0, 15]
        },
        {
          icon: '📏',
          text: '确保活动空间充足，周围至少2米的安全距离',
          timeRange: [15, 30]
        },
        {
          icon: '📹',
          text: '调整摄像头位置，确保能完整拍摄到您的身体',
          timeRange: [30, 45]
        },
        {
          icon: '💧',
          text: '准备充足的饮用水，运动过程中注意补充水分',
          timeRange: [45, 60]
        },
        {
          icon: '⚠️',
          text: '如有身体不适请立即停止，量力而行确保安全',
          timeRange: [60, 75]
        }
      ],
      // 准备事项清单
      checklistItems: [
        { text: '穿着舒适的运动服装', completed: false },
        { text: '确保活动空间充足', completed: false },
        { text: '摄像头可以完整拍摄到身体', completed: false },
        { text: '准备充足的饮用水', completed: false },
        { text: '身体状态良好，无不适感', completed: false }
      ]
    }
  },
  computed: {
    // 格式化训练时长
    formattedDuration () {
      const hours = Math.floor(this.trainingDuration / 3600)
      const minutes = Math.floor((this.trainingDuration % 3600) / 60)
      const seconds = this.trainingDuration % 60

      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`
      } else {
        return `${minutes}:${seconds.toString().padStart(2, '0')}`
      }
    },
    // 已训练动作数
    completedCount () {
      return this.completedCourses.length
    },
    // 总动作数
    totalCount () {
      return this.courses.length
    },
    // 训练进度百分比
    trainingProgress () {
      if (this.totalCount === 0) return 0
      return Math.round((this.completedCount / this.totalCount) * 100)
    },
    // 是否全部完成
    isAllCompleted () {
      return this.totalCount > 0 && this.completedCount >= this.totalCount
    },

    // 总运动时长（倒计时显示，mm:ss格式）
    totalExerciseTime () {
      // 测试模式或有方案数据时才显示
      if (
        !this.isTestMode &&
        (!this.data || !this.data.plan || !this.data.plan.exerciseTime)
      ) {
        return '00:00'
      }

      // 获取剩余时间（秒）
      const remainingSeconds = this.countdownRemaining

      // 转换为分钟和秒钟
      const minutes = Math.floor(remainingSeconds / 60)
      const seconds = remainingSeconds % 60

      // 格式化为 mm:ss
      return `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`
    },

    // 倒计时是否快结束（剩余时间少于5分钟时变红）
    isCountdownCritical () {
      return this.countdownRemaining > 0 && this.countdownRemaining <= 300 // 5分钟 = 300秒
    },

    // 倒计时是否非常紧急（剩余时间少于1分钟时闪烁）
    isCountdownUrgent () {
      return this.countdownRemaining > 0 && this.countdownRemaining <= 60 // 1分钟 = 60秒
    },

    // 当前视频需要的总次数（训练方案总时长/视频数量/当前视频时长）
    currentVideoRequiredCount () {
      if (
        !this.playingCourse ||
        !this.playingCourse.duration ||
        !this.courses ||
        this.courses.length === 0
      ) {
        return 0
      }

      let totalTimeInSeconds = 0
      if (this.isTestMode) {
        totalTimeInSeconds = 10 // 测试模式：10秒
      } else if (this.data && this.data.plan && this.data.plan.exerciseTime) {
        totalTimeInSeconds = this.data.plan.exerciseTime * 60 // 分钟转换为秒
      }

      if (totalTimeInSeconds <= 0) {
        return 0
      }

      // 计算每个视频分配到的时间 = 训练方案总时长 / 视频数量
      const timePerVideo = totalTimeInSeconds / this.courses.length

      // 将视频时长从毫秒转换为秒
      const videoDurationInSeconds = this.playingCourse.duration / 1000

      // 计算当前视频需要的次数 = 每个视频分配时间 / 当前视频时长（向上取整）
      const requiredCount = Math.ceil(timePerVideo / videoDurationInSeconds)

      console.log(`📊 当前视频动作次数计算:
        - 训练方案总时长: ${totalTimeInSeconds}秒
        - 视频总数量: ${this.courses.length}个
        - 每个视频分配时间: ${Math.round(timePerVideo)}秒
        - 当前视频时长: ${this.playingCourse.duration}ms (${Math.round(
        videoDurationInSeconds
      )}秒)
        - 需要循环次数: ${requiredCount}次`)

      return requiredCount
    },

    // 当前视频动作完成情况的显示文本
    currentVideoProgressText () {
      const completed = this.currentVideoLoopCount
      const required = this.currentVideoRequiredCount

      if (required <= 0) {
        return ''
      }

      return `${completed}/${required}次`
    }
  },
  watch: {
    // 监听课程切换，只在训练活跃时自动播放
    playingCourse (val) {
      // 只有在训练活跃状态下才自动播放视频
      if (this.isTrainingActive) {
        this.$nextTick(() => {
          const video = this.$refs.mainVideo
          if (video && video.play) {
            video.currentTime = 0
            video.play().catch(() => { })
          }
        })
      }
    }
  },
  async mounted () {
    console.log('mounted', this.$route.query)
    this.phone = this.$route.query.phone
    this.planId = this.$route.query.planId

    // 检查测试模式参数
    if (this.$route.query.testMode === 'true') {
      this.isTestMode = true
      console.log('🧪 测试模式已启用：总运动时长将设置为10秒')
    } else {
      console.log(
        '💡 提示：在URL中添加 &testMode=true 可启用测试模式（10秒倒计时）'
      )
    }

    try {
      // const urlParams = new URLSearchParams(window.location.search)
      if (this.$route.query.isApp) {
        this.isApp = true
      }

      // this.isApp = urlParams.get('isApp') === 'true'

      // 等待DOM完全渲染
      await this.$nextTick()
      // 添加额外的延迟确保DOM完全准备好
      await new Promise((resolve) => setTimeout(resolve, 100))

      // 初始化mediaDevices API
      initMediaDevices()
      this.isCameraSupported = true

      // 检查是否在安全上下文中运行
      if (!this.isSecureContext()) {
        const currentProtocol = window.location.protocol
        const currentHost = window.location.hostname

        if (currentProtocol === 'http:') {
          throw new Error(`当前使用的是不安全的HTTP协议(${currentProtocol})，请使用HTTPS协议访问此页面以启用摄像头功能。
如果您是在本地开发环境，请使用localhost或127.0.0.1访问。`)
        } else {
          throw new Error(`当前环境(${currentProtocol}//${currentHost})不是安全上下文，无法使用摄像头功能。
请确保使用HTTPS协议或通过localhost访问。`)
        }
      }

      // 检查是否支持getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('您的浏览器不支持摄像头功能，请使用现代浏览器访问')
      }

      // 先初始化TensorFlow和模型
      this.loadingMessage = '正在设置TensorFlow后端...'
      await tf.setBackend('webgl')
      console.log('TensorFlow后端设置完成:', tf.getBackend())

      this.loadingMessage = '正在加载AI模型...'
      await this.loadModel()
      console.log('AI模型加载成功')

      // 检查摄像头权限状态
      try {
        // 先检查是否有可用的视频输入设备
        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter(
          (device) => device.kind === 'videoinput'
        )

        if (videoDevices.length === 0) {
          throw new Error('未检测到摄像头设备，请确保摄像头已正确连接')
        }

        // 检查是否已有标签信息(表示已授权)
        const hasPermission = videoDevices.some((device) => device.label)

        if (!hasPermission) {
          // 如果没有权限,直接显示权限请求界面
          console.log('需要请求摄像头权限')
          this.needPermission = true
          this.isLoading = false
          return
        }

        // 如果已有权限,继续初始化
        console.log('已有摄像头权限,继续初始化')
        this.needPermission = false
        await this.initializeSystem()
        if (this.phone && this.planId) {
          console.log('方案跟练模式')
          // 弹出提示：方案跟练模式
          // this.$toast.show('方案跟练模式')
        }
      } catch (error) {
        console.error('权限检查失败:', error)
        this.handleError(error)
      }

      // 新增：加载方案和课程
      await this.loadPlanAndCourses()

      // 检查是否有保存的训练状态
      const savedTrainingState = this.checkSavedTrainingState()
      if (savedTrainingState) {
        console.log('🔍 发现上次中断的训练，显示恢复对话框')
        this.showRecoveryConfirmDialog()
      } else {
        // 没有保存的状态，正常开始训练
        this.startNormalTraining()
      }

      // 初始化完成后设置浏览器返回控制
      this.setupBrowserBackControl()
    } catch (error) {
      console.error('初始化失败:', error)
      this.errorMessage = error.message
      this.isLoading = false
    } finally {
      this.hideLoading()
    }
  },
  beforeDestroy () {
    // 记录当前播放的视频时长
    this.recordCurrentVideoPlayTime('页面卸载')

    // 最后一次保存训练状态
    if (this.isTrainingActive) {
      this.saveTrainingState('页面销毁')
    }

    // 停止自动保存
    this.stopAutoSave()

    // 移除浏览器返回控制监听器
    this.removeBrowserBackControl()

    this.cleanup()
    this.stopTrainingTimer()

    // 清理休息计时器
    if (this.restTimer) {
      clearInterval(this.restTimer)
      this.restTimer = null
    }

    // 清理循环间隔休息计时器
    if (this.loopRestTimer) {
      clearInterval(this.loopRestTimer)
      this.loopRestTimer = null
    }

    // 清理倒计时定时器
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
    }

    // 清理数字人视频重播定时器并停止播放
    if (this.digitalHumanVideoReplayTimer) {
      clearTimeout(this.digitalHumanVideoReplayTimer)
      this.digitalHumanVideoReplayTimer = null
    }

    // 确保数字人视频完全停止
    this.stopDigitalHumanVideo()
  },
  methods: {
    hideLoading () {
      this.$refs.container.stopLoading()
    },

    // ==================== 数字人视频相关方法 ====================
    //
    // 修复说明：移除了所有数字人视频的autoplay属性，改为使用v-if条件渲染
    // 这样确保视频元素只有在对话框真正显示时才会被创建和加载
    // 避免了页面加载时所有视频同时播放的问题
    //

    // 播放/暂停数字人视频
    toggleDigitalHumanVideo () {
      const video = this.$refs.digitalHumanVideo
      if (!video) {
        console.warn('数字人视频元素未找到，可能对话框未显示')
        return
      }

      // 清除重播定时器，防止干扰用户操作
      if (this.digitalHumanVideoReplayTimer) {
        clearTimeout(this.digitalHumanVideoReplayTimer)
        this.digitalHumanVideoReplayTimer = null
      }

      if (this.isDigitalHumanVideoPlaying) {
        video.pause()
      } else {
        video.play().catch((error) => {
          console.error('数字人视频播放失败:', error)
        })
      }
    },

    // 切换数字人视频静音状态
    toggleDigitalHumanVideoMute () {
      const video = this.$refs.digitalHumanVideo
      if (!video) return

      this.isDigitalHumanVideoMuted = !this.isDigitalHumanVideoMuted
      video.muted = this.isDigitalHumanVideoMuted
    },

    // 数字人视频加载完成
    onDigitalHumanVideoLoaded () {
      const video = this.$refs.digitalHumanVideo
      if (!video) return

      this.updateDigitalHumanVideoProgressText()
      console.log('数字人视频加载完成，等待3秒后播放')
    },

    // 更新视频进度文字
    updateDigitalHumanVideoProgressText () {
      const video = this.$refs.digitalHumanVideo
      if (!video) return

      const formatTime = (time) => {
        const minutes = Math.floor(time / 60)
        const seconds = Math.floor(time % 60)
        return `${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`
      }

      const currentTime = formatTime(video.currentTime)
      const duration = formatTime(video.duration || 0)
      this.digitalHumanVideoProgressText = `${currentTime} / ${duration}`
    },

    // 更新播报内容高亮
    updateBroadcastContent (currentTime) {
      // 根据视频播放时间，高亮显示对应的播报内容
      for (let i = 0; i < this.broadcastItems.length; i++) {
        const item = this.broadcastItems[i]
        if (
          currentTime >= item.timeRange[0] &&
          currentTime < item.timeRange[1]
        ) {
          this.currentBroadcastIndex = i
          break
        }
      }
    },

    // 更新清单完成进度（已移除清单功能，保留方法避免报错）
    updateChecklistProgress (currentTime) {
      // 清单功能已移除，此方法保留为空
    },

    // 判断当前播报项是否高亮
    isCurrentBroadcastItem (index) {
      return index === this.currentBroadcastIndex
    },

    // 显示准备运动弹窗（修改原有方法，包含数字人视频）
    showPrepareForExercise () {
      console.log('🏃‍♂️ 显示运动准备弹窗')
      this.showPrepareDialog = true

      // 清除之前可能存在的重播定时器
      if (this.digitalHumanVideoReplayTimer) {
        clearTimeout(this.digitalHumanVideoReplayTimer)
        this.digitalHumanVideoReplayTimer = null
      }

      // 重置数字人视频状态
      this.digitalHumanVideoProgress = 0
      this.digitalHumanVideoPlayCount = 0 // 重置播放次数计数器
      this.currentBroadcastIndex = 0
      this.isDigitalHumanVideoPlaying = false
      // this.showCountdown = true
      this.countdownSeconds = 1

      // 延迟一帧确保DOM完全渲染后再初始化视频
      this.$nextTick(() => {
        const video = this.$refs.digitalHumanVideo
        if (video) {
          // 添加事件监听
          video.addEventListener('play', () => {
            this.isDigitalHumanVideoPlaying = true
          })
          video.addEventListener('pause', () => {
            this.isDigitalHumanVideoPlaying = false
          })

          // 延迟1s执行
          setTimeout(() => {
            this.startDigitalHumanVideo()
          }, 1000)
        }
      })
    },

    // 开始倒计时
    startCountdown () {
      this.countdownTimer = setInterval(() => {
        this.countdownSeconds--
        if (this.countdownSeconds <= 0) {
          clearInterval(this.countdownTimer)
          this.showCountdown = false
          console.log('倒计时结束，开始播放数字人视频')
          this.startDigitalHumanVideo()
        }
      }, 1000)
    },

    // 启动数字人视频播放
    startDigitalHumanVideo () {
      const video = this.$refs.digitalHumanVideo
      if (!video) return

      // 设置静音状态并尝试自动播放
      video.muted = false
      video
        .play()
        .then(() => {
          console.log('数字人视频播放成功')
          this.isDigitalHumanVideoPlaying = true
        })
        .catch((error) => {
          console.error('数字人视频播放失败:', error)
        })
    },

    cleanup () {
      if (this.$refs.video && this.$refs.video.srcObject) {
        const tracks = this.$refs.video.srcObject.getTracks()
        tracks.forEach((track) => track.stop())
      }
    },

    isSecureContext () {
      const isSecure =
        window.isSecureContext ||
        window.location.protocol === 'https:' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'

      // 添加调试信息
      console.log('安全上下文检查:', {
        isSecureContext: window.isSecureContext,
        protocol: window.location.protocol,
        hostname: window.location.hostname,
        result: isSecure
      })

      return isSecure
    },

    async requestPermission () {
      try {
        this.needPermission = false
        this.isLoading = true
        this.loadingMessage = '正在请求摄像头权限...'
        this.errorMessage = '' // 清除之前的错误信息

        // 请求摄像头权限
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            frameRate: { ideal: 30 }
          }
        })

        // 保持流活跃一段时间，确保权限状态更新
        console.log('摄像头权限授予成功，保持流活跃...')

        // 添加短暂延迟，确保浏览器有足够时间更新权限状态
        await new Promise((resolve) => setTimeout(resolve, 500))

        // 再次检查权限状态
        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter(
          (device) => device.kind === 'videoinput'
        )
        const hasPermission = videoDevices.some((device) => device.label)

        console.log('权限检查状态:', {
          设备数量: videoDevices.length,
          有标签设备: videoDevices.filter((d) => d.label).length,
          权限状态: hasPermission
        })

        // 如果仍然没有权限，但getUserMedia成功，我们直接继续
        if (!hasPermission) {
          console.warn(
            '权限检查显示未获得权限，但getUserMedia已成功，继续初始化'
          )
          // 不再抛出错误，而是尝试继续初始化
        }

        // 继续初始化系统
        this.loadingMessage = '正在初始化系统...'

        // 此时再停止流（确保初始化前流仍然活跃）
        stream.getTracks().forEach((track) => track.stop())

        await this.initializeSystem()

        // 权限授权后需要重新加载方案和课程数据
        this.loadingMessage = '正在加载训练方案...'
        await this.loadPlanAndCourses()
      } catch (error) {
        console.error('请求权限失败:', error)
        this.handleError(error)
        // 重置到权限请求状态
        this.needPermission = true
        this.isLoading = false
      }
    },

    async initializeSystem () {
      try {
        // 确保DOM已经准备好
        await this.$nextTick()

        // 检查video元素是否存在
        if (!this.$refs.video) {
          console.error('Video元素不存在')
          throw new Error('初始化失败：视频元素未找到，请刷新页面重试')
        }

        this.loadingMessage = '正在初始化摄像头...'
        await this.initCamera()
        console.log('摄像头初始化成功')

        // 重置错误状态
        this.errorMessage = ''
        this.isLoading = false
        this.needPermission = false

        // 初始化完成后，显示准备弹窗而不是立即开始训练
        this.showPrepareForExercise()

        // 如果课程数据还没有加载，则在这里加载
        if (this.courses.length === 0 && this.planId) {
          console.log('📚 检测到课程数据未加载，开始加载...')
          this.loadingMessage = '正在加载训练方案...'
          await this.loadPlanAndCourses()
        }
      } catch (error) {
        console.error('系统初始化失败:', error)
        this.handleError(error)
      }
    },

    handleError (error) {
      this.isLoading = false
      console.error('错误详情:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      })

      // 如果是视频元素未找到的错误,自动重试
      if (error.message.includes('视频元素未找到')) {
        console.log('检测到视频元素未找到错误,自动重试初始化...')
        // 使用setTimeout确保DOM有时间更新
        setTimeout(() => {
          this.retryInitialization()
        }, 500)
        return
      }

      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        this.errorMessage = '无法访问摄像头，请确保已授予权限'
        this.needPermission = true // 显示权限请求界面
      } else if (
        error.name === 'NotFoundError' ||
        error.name === 'DevicesNotFoundError'
      ) {
        this.errorMessage = '未检测到摄像头设备'
      } else if (
        error.name === 'NotReadableError' ||
        error.name === 'TrackStartError'
      ) {
        this.errorMessage = '摄像头被其他应用程序占用'
      } else if (error.message.includes('model')) {
        this.errorMessage = '模型加载失败，请检查网络连接'
      } else {
        this.errorMessage = '初始化失败: ' + error.message
      }
    },

    async retryInitialization () {
      try {
        // 重置所有状态
        this.errorMessage = ''
        this.isLoading = true
        this.loadingMessage = '正在重新初始化...'
        this.cleanup()

        // 确保DOM完全准备好
        await new Promise((resolve) => setTimeout(resolve, 100))
        await this.$nextTick()

        // 重新初始化mediaDevices
        initMediaDevices()

        // 检查摄像头权限
        try {
          const devices = await navigator.mediaDevices.enumerateDevices()
          const videoDevices = devices.filter(
            (device) => device.kind === 'videoinput'
          )
          const hasPermission = videoDevices.some((device) => device.label)

          if (!hasPermission) {
            console.log('需要请求摄像头权限')
            this.needPermission = true
            this.isLoading = false
            return
          }

          // 如果已有权限,继续初始化
          console.log('已有摄像头权限,继续初始化')
          this.needPermission = false
          this.isCameraSupported = true
          await this.initializeSystem()

          // 重试时也需要确保课程数据已加载
          if (this.courses.length === 0 && this.planId) {
            console.log('📚 重试时检测到课程数据未加载，开始加载...')
            this.loadingMessage = '正在加载训练方案...'
            await this.loadPlanAndCourses()
          }
        } catch (error) {
          console.error('权限检查失败:', error)
          this.handleError(error)
        }
      } catch (error) {
        console.error('重试初始化失败:', error)
        this.handleError(error)
      }
    },

    async loadModel () {
      try {
        this.detector = await poseDetection.createDetector(
          poseDetection.SupportedModels.MoveNet,
          this.detectorConfig
        )
        this.isModelLoaded = true
      } catch (error) {
        console.error('模型加载失败:', error)
        throw new Error('模型加载失败，请检查网络连接')
      }
    },

    async initCamera () {
      try {
        // 先检查是否有可用的视频输入设备
        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter(
          (device) => device.kind === 'videoinput'
        )

        console.log(
          '检测到的视频设备:',
          videoDevices.map((device) => ({
            deviceId: device.deviceId,
            label: device.label || '未命名设备',
            groupId: device.groupId
          }))
        )

        if (videoDevices.length === 0) {
          throw new Error('未检测到摄像头设备，请确保摄像头已正确连接')
        }

        // 尝试使用第一个可用的摄像头
        const firstDevice = videoDevices[0]
        console.log('尝试使用摄像头:', firstDevice.label || '未命名设备')

        const constraints = {
          video: {
            deviceId: firstDevice.deviceId
              ? { exact: firstDevice.deviceId }
              : undefined,
            width: { ideal: 640 },
            height: { ideal: 480 },
            frameRate: { ideal: 30 }
          },
          audio: false
        }

        console.log('使用以下配置请求摄像头:', constraints)

        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        console.log('成功获取视频流')

        const videoElement = this.$refs.video
        if (!videoElement) {
          throw new Error('视频元素未找到')
        }

        // 设置视频源
        videoElement.srcObject = stream
        videoElement.style.transform = 'scaleX(-1)' // 镜像显示

        // 等待视频元数据加载和播放就绪
        await new Promise((resolve, reject) => {
          const maxAttempts = 3 // 最大重试次数
          let attempts = 0

          const tryLoadVideo = () => {
            attempts++
            console.log(`尝试加载视频 (第${attempts}次)`)

            const timeout = setTimeout(() => {
              if (attempts >= maxAttempts) {
                cleanup()
                reject(new Error('视频元数据加载超时'))
              } else {
                console.log('重试加载视频...')
                cleanup()
                tryLoadVideo()
              }
            }, 15000) // 延长到15秒

            const cleanup = () => {
              videoElement.removeEventListener(
                'loadedmetadata',
                handleLoadedMetadata
              )
              videoElement.removeEventListener('loadeddata', handleLoadedData)
              videoElement.removeEventListener('canplay', handleCanPlay)
              videoElement.removeEventListener('error', handleError)
              clearTimeout(timeout)
            }

            const handleLoadedMetadata = () => {
              console.log('视频元数据加载完成')
            }

            const handleLoadedData = () => {
              console.log('视频数据加载完成')
            }

            const handleCanPlay = () => {
              console.log('视频可以播放')
              cleanup()
              resolve()
            }

            const handleError = (error) => {
              console.error('视频加载错误:', error)
              cleanup()
              if (attempts >= maxAttempts) {
                reject(
                  new Error(`视频加载失败: ${error.message || '未知错误'}`)
                )
              } else {
                tryLoadVideo()
              }
            }

            videoElement.addEventListener(
              'loadedmetadata',
              handleLoadedMetadata
            )
            videoElement.addEventListener('loadeddata', handleLoadedData)
            videoElement.addEventListener('canplay', handleCanPlay)
            videoElement.addEventListener('error', handleError)
          }

          tryLoadVideo()
        })

        // 尝试播放视频
        try {
          await videoElement.play()
          console.log('视频开始播放')
        } catch (error) {
          console.error('视频播放失败:', error)
          throw new Error(`视频播放失败: ${error.message}`)
        }

        // 设置canvas尺寸
        const canvas = this.$refs.canvas
        if (!canvas) {
          throw new Error('Canvas元素未找到')
        }

        // 获取canvas在页面中的实际显示尺寸
        const canvasRect = canvas.getBoundingClientRect()
        canvas.width = canvasRect.width
        canvas.height = canvasRect.height

        console.log(
          '摄像头初始化完成 - 分辨率:',
          canvas.width,
          'x',
          canvas.height
        )
      } catch (error) {
        console.error('摄像头初始化失败:', error)

        // 检查是否是权限问题
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError'
        ) {
          this.needPermission = true
          throw new Error(
            '无法访问摄像头，请在Chrome设置中允许网站访问摄像头\n设置路径：设置 -> 隐私和安全 -> 网站设置 -> 摄像头'
          )
        }

        // 检查是否是设备被占用
        if (
          error.name === 'NotReadableError' ||
          error.name === 'TrackStartError'
        ) {
          throw new Error(
            '摄像头可能被其他程序占用，请关闭其他使用摄像头的程序后重试'
          )
        }

        // 其他错误
        throw new Error(
          `摄像头初始化失败: ${error.message}\n请确保摄像头已正确连接并且驱动程序已安装`
        )
      }
    },

    async detectPose () {
      // 只有当训练活跃时才进行姿态检测和计数
      if (
        !this.isTrainingActive ||
        !this.detector ||
        !this.$refs.video ||
        !this.isModelLoaded
      ) {
        // 如果训练不活跃，确保停止检测循环
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId)
          this.animationFrameId = null
          console.log('姿态检测已停止 (因训练不活跃)')
        }
        return
      }

      try {
        // 进行姿态检测
        const poses = await this.detector.estimatePoses(this.$refs.video, {
          flipHorizontal: false
        })

        if (poses.length > 0) {
          // 新增：多人检测时选择中心位置的人
          let selectedPose = poses[0] // 默认选择第一个

          if (poses.length > 1) {
            console.log(`检测到${poses.length}个人，正在选择中心位置的人...`)
            selectedPose = this.selectCenterPerson(poses)
          }

          const keypoints = selectedPose.keypoints

          // 绘制骨骼点
          this.drawKeypoints(keypoints)

          // 检测运动
          if (selectedPose.score > this.detectorConfig.minPoseScore) {
            this.detectMovement(keypoints)
          }

          // 更新上一帧关键点
          this.lastKeypoints = keypoints

          // 重置无人检测计数
          this.noPersonFrameCount = 0
        } else {
          // 新增：无人检测处理
          this.noPersonFrameCount++

          // 如果连续多帧无人检测，清除跟踪信息
          if (this.noPersonFrameCount >= this.maxNoPersonFrames) {
            this.lastSelectedPersonCenter = null
            console.log('📭 连续无人检测，清除跟踪信息')
          }

          // 清除画布
          if (this.$refs.canvas) {
            const ctx = this.$refs.canvas.getContext('2d')
            const { width, height } = this.$refs.canvas
            ctx.clearRect(0, 0, width, height)
          }
        }

        // 只有当训练活跃时才继续下一帧检测
        if (this.isTrainingActive) {
          this.animationFrameId = requestAnimationFrame(() => this.detectPose())
        } else {
          console.log('姿态检测循环已停止 (训练不活跃)')
          this.animationFrameId = null // 确保清除 ID
        }
      } catch (error) {
        console.error('姿态检测错误:', error)
        this.errorMessage = '姿态检测出错，请刷新页面重试'
      }
    },

    // 新增：选择最接近画面中心的人
    selectCenterPerson (poses) {
      if (!poses || poses.length === 0) return null
      if (poses.length === 1) return poses[0]

      const videoElement = this.$refs.video
      if (!videoElement) return poses[0]

      // 获取视频画面的中心点
      const centerX = (videoElement.videoWidth || 640) / 2
      const centerY = (videoElement.videoHeight || 480) / 2

      let bestPose = poses[0]
      let minDistance = Infinity
      let trackingPose = null // 用于跟踪的人物

      poses.forEach((pose, index) => {
        // 计算人体中心点（使用多个关键点的平均位置）
        const personCenter = this.calculatePersonCenter(pose.keypoints)

        if (personCenter) {
          // 计算与画面中心的距离
          const distanceToCenter = Math.sqrt(
            Math.pow(personCenter.x - centerX, 2) +
            Math.pow(personCenter.y - centerY, 2)
          )

          // 如果有上一帧的人物位置，优先考虑跟踪一致性
          if (this.lastSelectedPersonCenter) {
            const trackingDistance = Math.sqrt(
              Math.pow(personCenter.x - this.lastSelectedPersonCenter.x, 2) +
              Math.pow(personCenter.y - this.lastSelectedPersonCenter.y, 2)
            )

            // 如果在跟踪阈值内，优先选择这个人物
            if (
              trackingDistance < this.personTrackingThreshold &&
              pose.score > this.detectorConfig.minPoseScore
            ) {
              trackingPose = pose
              console.log(
                `🎯 人物${index + 1}: 跟踪距离${Math.round(
                  trackingDistance
                )}像素 (优先选择)`
              )
            }
          }

          console.log(
            `人物${index + 1}: 中心点(${Math.round(
              personCenter.x
            )}, ${Math.round(personCenter.y)}), 与画面中心距离: ${Math.round(
              distanceToCenter
            )}, 置信度: ${pose.score.toFixed(3)}`
          )

          // 选择距离最近且置信度足够的人
          if (
            distanceToCenter < minDistance &&
            pose.score > this.detectorConfig.minPoseScore
          ) {
            minDistance = distanceToCenter
            bestPose = pose
          }
        }
      })

      // 优先使用跟踪的人物，否则使用最接近中心的人物
      const selectedPose = trackingPose || bestPose
      const selectedPersonCenter = this.calculatePersonCenter(
        selectedPose.keypoints
      )

      if (selectedPersonCenter) {
        this.lastSelectedPersonCenter = selectedPersonCenter
        this.noPersonFrameCount = 0 // 重置无人计数

        if (trackingPose) {
          console.log('✅ 选择了跟踪的人物 (保持一致性)')
        } else {
          console.log(
            `✅ 选择了距离画面中心最近的人，距离: ${Math.round(
              minDistance
            )}像素`
          )
        }
      }

      return selectedPose
    },

    // 新增：计算人体中心点
    calculatePersonCenter (keypoints) {
      if (!keypoints || keypoints.length === 0) return null

      // 使用躯干关键点计算中心（更稳定）
      const torsoKeypoints = [
        'left_shoulder',
        'right_shoulder',
        'left_hip',
        'right_hip',
        'nose' // 头部作为参考
      ]

      let validPoints = []
      let totalX = 0
      let totalY = 0

      torsoKeypoints.forEach((pointName) => {
        const point = keypoints.find((kp) => kp.name === pointName)
        if (point && point.score > this.detectorConfig.minPoseScore) {
          validPoints.push(point)
          totalX += point.x
          totalY += point.y
        }
      })

      // 如果躯干关键点不足，使用所有可信的关键点
      if (validPoints.length < 3) {
        validPoints = []
        totalX = 0
        totalY = 0

        keypoints.forEach((point) => {
          if (point.score > this.detectorConfig.minPoseScore) {
            validPoints.push(point)
            totalX += point.x
            totalY += point.y
          }
        })
      }

      if (validPoints.length === 0) return null

      return {
        x: totalX / validPoints.length,
        y: totalY / validPoints.length,
        validPointsCount: validPoints.length
      }
    },

    drawKeypoints (keypoints) {
      const ctx = this.$refs.canvas.getContext('2d')
      const { width, height } = this.$refs.canvas

      // 清除画布
      ctx.clearRect(0, 0, width, height)

      // 获取视频元素尺寸用于坐标缩放
      const videoElement = this.$refs.video
      if (!videoElement) return

      const scaleX = width / (videoElement.videoWidth || 640)
      const scaleY = height / (videoElement.videoHeight || 480)

      // 绘制关键点（应用镜像坐标变换）
      keypoints.forEach((point) => {
        if (point.score > this.detectorConfig.minPoseScore) {
          // 镜像X坐标：width - (point.x * scaleX)
          const mirroredX = width - point.x * scaleX
          const y = point.y * scaleY

          ctx.beginPath()
          ctx.arc(mirroredX, y, 5, 0, 2 * Math.PI)
          ctx.fillStyle = 'red'
          ctx.fill()
        }
      })

      // 绘制骨骼连线（传入镜像后的坐标处理函数）
      this.drawSkeleton(keypoints, ctx, scaleX, scaleY, width)

      // 新增：调试模式下的可视化
      if (this.debugMode) {
        // 绘制画面中心点
        const centerX = width / 2
        const centerY = height / 2
        ctx.beginPath()
        ctx.arc(centerX, centerY, 8, 0, 2 * Math.PI)
        ctx.fillStyle = 'blue'
        ctx.fill()

        // 添加中心点标签（文字现在不会被镜像）
        ctx.fillStyle = 'white'
        ctx.font = '12px Arial'
        ctx.fillText('画面中心', centerX + 12, centerY + 4)

        // 绘制当前人物中心点
        const personCenter = this.calculatePersonCenter(keypoints)
        if (personCenter) {
          // 应用镜像坐标变换
          const mirroredPersonX = width - personCenter.x * scaleX
          const personY = personCenter.y * scaleY

          ctx.beginPath()
          ctx.arc(mirroredPersonX, personY, 10, 0, 2 * Math.PI)
          ctx.strokeStyle = 'yellow'
          ctx.lineWidth = 3
          ctx.stroke()

          // 添加人物中心点标签（文字现在不会被镜像）
          ctx.fillStyle = 'yellow'
          ctx.fillText('人物中心', mirroredPersonX + 12, personY - 8)

          // 绘制连接线
          ctx.beginPath()
          ctx.moveTo(centerX, centerY)
          ctx.lineTo(mirroredPersonX, personY)
          ctx.strokeStyle = 'rgba(255, 255, 0, 0.5)'
          ctx.lineWidth = 2
          ctx.stroke()

          // 显示距离信息（文字现在不会被镜像）
          const distance = Math.sqrt(
            Math.pow(mirroredPersonX - centerX, 2) +
            Math.pow(personY - centerY, 2)
          )
          ctx.fillStyle = 'white'
          ctx.fillText(`距离: ${Math.round(distance)}px`, 10, 20)
        }
      }
    },

    drawSkeleton (keypoints, ctx, scaleX, scaleY, canvasWidth) {
      const connections = [
        ['nose', 'left_eye'],
        ['nose', 'right_eye'],
        ['left_eye', 'left_ear'],
        ['right_eye', 'right_ear'],
        ['left_shoulder', 'right_shoulder'],
        ['left_shoulder', 'left_elbow'],
        ['right_shoulder', 'right_elbow'],
        ['left_elbow', 'left_wrist'],
        ['right_elbow', 'right_wrist'],
        ['left_shoulder', 'left_hip'],
        ['right_shoulder', 'right_hip'],
        ['left_hip', 'right_hip'],
        ['left_hip', 'left_knee'],
        ['right_hip', 'right_knee'],
        ['left_knee', 'left_ankle'],
        ['right_knee', 'right_ankle']
      ]

      ctx.strokeStyle = 'white'
      ctx.lineWidth = 2

      connections.forEach(([p1, p2]) => {
        const point1 = keypoints.find((p) => p.name === p1)
        const point2 = keypoints.find((p) => p.name === p2)

        if (
          point1 &&
          point2 &&
          point1.score > this.detectorConfig.minPoseScore &&
          point2.score > this.detectorConfig.minPoseScore
        ) {
          // 应用镜像坐标变换
          const mirroredX1 = canvasWidth - point1.x * scaleX
          const y1 = point1.y * scaleY
          const mirroredX2 = canvasWidth - point2.x * scaleX
          const y2 = point2.y * scaleY

          ctx.beginPath()
          ctx.moveTo(mirroredX1, y1)
          ctx.lineTo(mirroredX2, y2)
          ctx.stroke()
        }
      })
    },

    // 主运动检测方法（优化后避免重复计数）
    detectMovement (currentKeypoints) {
      if (!this.lastKeypoints) return

      // 检测手臂运动（采用智能算法避免重复计数）
      this.detectArmMovement(currentKeypoints)

      // 检测腿部运动（采用智能算法避免重复计数）
      this.detectLegMovement(currentKeypoints)

      // 更新进度条（仅在必要时更新）
      // this.updateProgressBars() // 移至具体计数方法中调用，避免频繁更新
    },

    // 优化后的手臂运动检测算法（支持简化模式）
    detectArmMovement (currentKeypoints) {
      const leftWrist = currentKeypoints.find((p) => p.name === 'left_wrist')
      const rightWrist = currentKeypoints.find((p) => p.name === 'right_wrist')
      const lastLeftWrist = this.lastKeypoints.find(
        (p) => p.name === 'left_wrist'
      )
      const lastRightWrist = this.lastKeypoints.find(
        (p) => p.name === 'right_wrist'
      )

      if (this.useSimpleDetection) {
        // 简化检测模式：降低检测要求，增加敏感度
        this.detectArmMovementSimple(
          leftWrist,
          lastLeftWrist,
          rightWrist,
          lastRightWrist
        )
      } else {
        // 检测左手腕运动
        if (
          leftWrist &&
          lastLeftWrist &&
          leftWrist.score > this.detectorConfig.minPoseScore &&
          lastLeftWrist.score > this.detectorConfig.minPoseScore
        ) {
          this.detectSingleLimbMovement(
            leftWrist,
            lastLeftWrist,
            this.armMovementState.left,
            'left_arm'
          )
        }

        // 检测右手腕运动
        if (
          rightWrist &&
          lastRightWrist &&
          rightWrist.score > this.detectorConfig.minPoseScore &&
          lastRightWrist.score > this.detectorConfig.minPoseScore
        ) {
          this.detectSingleLimbMovement(
            rightWrist,
            lastRightWrist,
            this.armMovementState.right,
            'right_arm'
          )
        }
      }
    },

    // 优化后的腿部运动检测算法（支持简化模式）
    detectLegMovement (currentKeypoints) {
      const leftAnkle = currentKeypoints.find((p) => p.name === 'left_ankle')
      const rightAnkle = currentKeypoints.find((p) => p.name === 'right_ankle')
      const lastLeftAnkle = this.lastKeypoints.find(
        (p) => p.name === 'left_ankle'
      )
      const lastRightAnkle = this.lastKeypoints.find(
        (p) => p.name === 'right_ankle'
      )

      if (this.useSimpleDetection) {
        // 简化检测模式：降低检测要求，增加敏感度
        this.detectLegMovementSimple(
          leftAnkle,
          lastLeftAnkle,
          rightAnkle,
          lastRightAnkle
        )
      } else {
        // 检测左腿运动
        if (
          leftAnkle &&
          lastLeftAnkle &&
          leftAnkle.score > this.detectorConfig.minPoseScore &&
          lastLeftAnkle.score > this.detectorConfig.minPoseScore
        ) {
          this.detectSingleLimbMovement(
            leftAnkle,
            lastLeftAnkle,
            this.legMovementState.left,
            'left_leg'
          )
        }

        // 检测右腿运动
        if (
          rightAnkle &&
          lastRightAnkle &&
          rightAnkle.score > this.detectorConfig.minPoseScore &&
          lastRightAnkle.score > this.detectorConfig.minPoseScore
        ) {
          this.detectSingleLimbMovement(
            rightAnkle,
            lastRightAnkle,
            this.legMovementState.right,
            'right_leg'
          )
        }
      }
    },

    // 新增：简化的手臂运动检测（解决检测过严问题）
    detectArmMovementSimple (
      leftWrist,
      lastLeftWrist,
      rightWrist,
      lastRightWrist
    ) {
      const currentTime = Date.now()

      // 检测左手腕
      if (
        leftWrist &&
        lastLeftWrist &&
        leftWrist.score > this.detectorConfig.minPoseScore &&
        lastLeftWrist.score > this.detectorConfig.minPoseScore
      ) {
        const leftDistance = Math.sqrt(
          Math.pow(leftWrist.y - lastLeftWrist.y, 2) +
          Math.pow(leftWrist.x - lastLeftWrist.x, 2)
        )
        if (leftDistance > this.movementThreshold * 0.8) {
          // 降低阈值20%
          const state = this.armMovementState.left
          if (currentTime - state.lastCountTime > 400) {
            // 400ms冷却时间
            this.armCount++
            state.lastCountTime = currentTime
            console.log(
              `✅ 简化模式-左手臂运动 +1, 距离: ${Math.round(
                leftDistance
              )}, 总计: ${this.armCount}`
            )
            this.updateProgressBars()
          }
        }
      }

      // 检测右手腕
      if (
        rightWrist &&
        lastRightWrist &&
        rightWrist.score > this.detectorConfig.minPoseScore &&
        lastRightWrist.score > this.detectorConfig.minPoseScore
      ) {
        const rightDistance = Math.sqrt(
          Math.pow(rightWrist.y - lastRightWrist.y, 2) +
          Math.pow(rightWrist.x - lastRightWrist.x, 2)
        )
        if (rightDistance > this.movementThreshold * 0.8) {
          // 降低阈值20%
          const state = this.armMovementState.right
          if (currentTime - state.lastCountTime > 400) {
            // 400ms冷却时间
            this.armCount++
            state.lastCountTime = currentTime
            console.log(
              `✅ 简化模式-右手臂运动 +1, 距离: ${Math.round(
                rightDistance
              )}, 总计: ${this.armCount}`
            )
            this.updateProgressBars()
          }
        }
      }
    },

    // 新增：简化的腿部运动检测
    detectLegMovementSimple (
      leftAnkle,
      lastLeftAnkle,
      rightAnkle,
      lastRightAnkle
    ) {
      const currentTime = Date.now()

      // 检测左腿
      if (
        leftAnkle &&
        lastLeftAnkle &&
        leftAnkle.score > this.detectorConfig.minPoseScore &&
        lastLeftAnkle.score > this.detectorConfig.minPoseScore
      ) {
        const leftDistance = Math.sqrt(
          Math.pow(leftAnkle.y - lastLeftAnkle.y, 2) +
          Math.pow(leftAnkle.x - lastLeftAnkle.x, 2)
        )
        if (leftDistance > this.movementThreshold * 0.8) {
          // 降低阈值20%
          const state = this.legMovementState.left
          if (currentTime - state.lastCountTime > 400) {
            // 400ms冷却时间
            this.legCount++
            state.lastCountTime = currentTime
            console.log(
              `✅ 简化模式-左腿运动 +1, 距离: ${Math.round(
                leftDistance
              )}, 总计: ${this.legCount}`
            )
            this.updateProgressBars()
          }
        }
      }

      // 检测右腿
      if (
        rightAnkle &&
        lastRightAnkle &&
        rightAnkle.score > this.detectorConfig.minPoseScore &&
        lastRightAnkle.score > this.detectorConfig.minPoseScore
      ) {
        const rightDistance = Math.sqrt(
          Math.pow(rightAnkle.y - lastRightAnkle.y, 2) +
          Math.pow(rightAnkle.x - lastRightAnkle.x, 2)
        )
        if (rightDistance > this.movementThreshold * 0.8) {
          // 降低阈值20%
          const state = this.legMovementState.right
          if (currentTime - state.lastCountTime > 400) {
            // 400ms冷却时间
            this.legCount++
            state.lastCountTime = currentTime
            console.log(
              `✅ 简化模式-右腿运动 +1, 距离: ${Math.round(
                rightDistance
              )}, 总计: ${this.legCount}`
            )
            this.updateProgressBars()
          }
        }
      }
    },

    // 新增：智能单肢体运动检测算法（核心优化）
    detectSingleLimbMovement (currentPoint, lastPoint, movementState, limbType) {
      const currentTime = Date.now()

      // 计算移动距离
      const distance = Math.sqrt(
        Math.pow(currentPoint.y - lastPoint.y, 2) +
        Math.pow(currentPoint.x - lastPoint.x, 2)
      )

      // 计算移动方向
      const deltaX = currentPoint.x - lastPoint.x
      const deltaY = currentPoint.y - lastPoint.y
      let currentDirection = null

      // 确定主要移动方向（更精确的方向判断）
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        currentDirection = deltaX > 0 ? 'right' : 'left'
      } else {
        currentDirection = deltaY > 0 ? 'down' : 'up'
      }

      // 检查是否在冷却期内
      const isInCooldown =
        currentTime - movementState.lastCountTime <
        movementState.cooldownDuration

      if (distance > this.movementThreshold) {
        // 开始运动状态
        if (!movementState.isMoving) {
          movementState.isMoving = true
          movementState.movementStartTime = currentTime
          movementState.lastDirection = currentDirection

          console.log(
            `🎯 开始检测${limbType}运动 - 方向: ${currentDirection}, 距离: ${Math.round(
              distance
            )}`
          )
        } else {
          // 检测方向变化（完成一次完整动作）
          if (
            movementState.lastDirection &&
            movementState.lastDirection !== currentDirection &&
            !isInCooldown
          ) {
            const movementDuration =
              currentTime - movementState.movementStartTime

            // 确保动作持续时间足够长（避免误检测）
            if (movementDuration >= movementState.minMovementDuration) {
              // 记录一次完整的动作
              if (limbType.includes('arm')) {
                this.armCount++
                console.log(
                  `✅ 记录手臂运动 +1 - ${limbType} (${movementState.lastDirection} → ${currentDirection}), 总计: ${this.armCount}`
                )
              } else if (limbType.includes('leg')) {
                this.legCount++
                console.log(
                  `✅ 记录腿部运动 +1 - ${limbType} (${movementState.lastDirection} → ${currentDirection}), 总计: ${this.legCount}`
                )
              }

              // 更新状态
              movementState.lastCountTime = currentTime
              movementState.lastDirection = currentDirection
              movementState.movementStartTime = currentTime

              // 触发进度条更新
              this.updateProgressBars()
            } else {
              // 运动时间太短，更新方向但不计数
              movementState.lastDirection = currentDirection
              console.log(
                `⚠️ ${limbType}运动时间过短(${movementDuration}ms < ${movementState.minMovementDuration}ms)，不计数`
              )
            }
          } else {
            // 同一方向运动，更新方向
            movementState.lastDirection = currentDirection
          }
        }
      } else {
        // 移动距离不够，重置运动状态
        if (movementState.isMoving) {
          const movementDuration = currentTime - movementState.movementStartTime

          // 如果运动时间很短就停止了，不认为是有效运动
          if (movementDuration < movementState.minMovementDuration) {
            console.log(
              `⏹️ ${limbType}运动结束 - 时间过短(${movementDuration}ms)，重置状态`
            )
          } else {
            console.log(
              `⏹️ ${limbType}运动结束 - 持续时间: ${movementDuration}ms`
            )
          }

          movementState.isMoving = false
          movementState.lastDirection = null
          movementState.movementStartTime = 0
        }
      }
    },

    // 新增：调试方法 - 显示运动状态
    showMovementStates () {
      console.log('🔍 当前运动状态:')
      console.log('手臂状态:', {
        左手: this.armMovementState.left,
        右手: this.armMovementState.right
      })
      console.log('腿部状态:', {
        左腿: this.legMovementState.left,
        右腿: this.legMovementState.right
      })
      console.log('计数统计:', {
        手臂运动: this.armCount,
        腿部运动: this.legCount,
        总运动: this.armCount + this.legCount
      })
    },

    // 新增：重置运动计数
    resetMovementCounts () {
      this.armCount = 0
      this.legCount = 0

      // 重置运动状态（使用优化后的参数）
      const resetState = {
        isMoving: false,
        lastDirection: null,
        lastCountTime: 0,
        movementStartTime: 0,
        minMovementDuration: 150,
        cooldownDuration: 300,
        movementPeaks: [],
        lastPosition: null
      }

      this.armMovementState.left = { ...resetState }
      this.armMovementState.right = { ...resetState }
      this.legMovementState.left = { ...resetState }
      this.legMovementState.right = { ...resetState }

      console.log(
        `🔄 运动计数已重置 (当前模式: ${this.useSimpleDetection ? '简化' : '智能'
        })`
      )
      this.updateProgressBars()
    },

    // 新增：调整运动检测参数
    adjustMovementDetectionParams (params = {}) {
      const {
        threshold = this.movementThreshold,
        minDuration = 150,
        cooldown = 300
      } = params

      this.movementThreshold = threshold

      // 更新所有肢体的参数
      const updateParams = (state) => {
        state.minMovementDuration = minDuration
        state.cooldownDuration = cooldown
      }

      updateParams(this.armMovementState.left)
      updateParams(this.armMovementState.right)
      updateParams(this.legMovementState.left)
      updateParams(this.legMovementState.right)

      console.log('⚙️ 运动检测参数已更新:', {
        移动阈值: threshold,
        最小持续时间: minDuration + 'ms',
        冷却时间: cooldown + 'ms'
      })
    },

    // 新增：快速切换检测模式
    toggleDetectionMode () {
      this.useSimpleDetection = !this.useSimpleDetection
      console.log(
        `🔄 检测模式已切换为: ${this.useSimpleDetection ? '简化模式' : '智能模式'
        }`
      )

      if (this.useSimpleDetection) {
        console.log('📢 简化模式特点:')
        console.log('   - 降低检测阈值 (移动距离要求降低20%)')
        console.log('   - 简化冷却时间 (400ms)')
        console.log('   - 更容易触发计数')
      } else {
        console.log('📢 智能模式特点:')
        console.log('   - 方向变化检测')
        console.log('   - 运动持续时间验证')
        console.log('   - 更精确但要求更高')
      }

      // 重置计数以避免模式切换时的异常
      this.resetMovementCounts()
    },

    // 新增：获取当前检测模式状态
    getDetectionModeInfo () {
      return {
        mode: this.useSimpleDetection ? '简化模式' : '智能模式',
        threshold: this.movementThreshold,
        adjustedThreshold: this.useSimpleDetection
          ? this.movementThreshold * 0.8
          : this.movementThreshold,
        cooldown: this.useSimpleDetection
          ? 400
          : this.armMovementState.left.cooldownDuration,
        description: this.useSimpleDetection
          ? '更容易检测到运动，适合调试和基础训练'
          : '精确检测完整动作，避免误计数'
      }
    },

    stopDetection () {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId)
        this.animationFrameId = null
        console.log('姿态检测已停止')
      }
    },

    startDetection () {
      if (this.isModelLoaded) {
        // 保存 requestAnimationFrame 的 ID
        this.animationFrameId = requestAnimationFrame(() => this.detectPose())
      }
    },

    // 新增：转换HTTP URL为HTTPS URL
    convertToHttps (url) {
      if (!url || typeof url !== 'string') {
        return url
      }

      // 如果URL以http:开头，转换为https:
      if (url.startsWith('http:')) {
        const httpsUrl = url.replace('http:', 'https:')
        console.log('🔒 URL协议转换:', url, '→', httpsUrl)
        return httpsUrl
      }

      return url
    },

    // 新增：处理课程URL转换
    processCourseUrls (course) {
      if (!course) return course

      // 转换视频URL
      if (course.videoUrl) {
        course.videoUrl = this.convertToHttps(course.videoUrl)
      }

      // 转换封面URL
      if (course.coverUrl) {
        course.coverUrl = this.convertToHttps(course.coverUrl)
      }

      return course
    },

    // 新增：加载方案和课程
    async loadPlanAndCourses () {
      try {
        if (!this.planId) {
          console.log('⚠️ 没有提供planId，跳过加载方案')
          return
        }

        console.log('📚 开始加载训练方案:', this.planId)

        // 加载方案详情
        const res = await PlanDao.loadPlanById(this.planId)
        console.log('🔍 加载方案详情:', res)
        if (!res) {
          this.errorMessage = '未找到训练方案'
          return
        }
        res.plan =
          typeof res.plan === 'string' ? JSON.parse(res.plan) : res.plan
        res.gmtCreate = DateUtils.formate(res.gmtCreate, 'yyyy-MM-DD HH:mm:ss')
        this.data = res

        console.log(
          '📚 方案加载成功:',
          res.plan?.planCourseBeans?.length,
          '个课程'
        )

        // 加载课程详情
        this.courses = []
        for (const e of res.plan.planCourseBeans) {
          console.log('📖 加载课程:', e.courseId)
          const course = await PlanDao.loadCourse(e.courseId)
          if (course) {
            // 处理URL转换
            const processedCourse = this.processCourseUrls(course)
            console.log(
              '📖 课程加载成功:',
              processedCourse.title,
              '(ID:',
              processedCourse.courseId,
              ')'
            )
            this.courses.push(processedCourse)
          } else {
            console.log('❌ 课程加载失败:', e.courseId)
          }
        }

        this.playingCourse = this.courses[0] || null
        this.loadingCourse = false

        console.log('📚 所有课程加载完成:', {
          总课程数: this.courses.length,
          课程列表: this.courses.map((c) => ({
            id: c.courseId,
            title: c.title
          })),
          当前播放: this.playingCourse?.title
        })
      } catch (err) {
        console.error('❌ 加载方案或课程失败:', err)
        this.errorMessage = '加载方案或课程失败: ' + (err.message || err)
      }
    },
    // 新增：切换播放课程
    handleCourseClick (item, index) {
      if (
        item.courseId === (this.playingCourse && this.playingCourse.courseId)
      ) {
        return
      }

      this.switchToCourse(item, index)
    },

    // 新增：切换到指定课程（抽取公共逻辑）
    switchToCourse (item, index, markCurrentAsCompleted = false) {
      // 记录当前视频的播放时长
      this.recordCurrentVideoPlayTime('课程切换')

      // 根据参数决定是否标记当前课程为已完成
      if (markCurrentAsCompleted) {
        this.markCurrentVideoAsCompleted('手动切换课程')
      }

      // 关键操作：课程切换，触发保存
      this.saveOnCriticalAction('课程切换')

      // 重置当前视频循环计数
      this.currentVideoLoopCount = 0
      console.log('🔄 切换视频，重置循环计数为 0')

      // 确保课程URL使用HTTPS协议
      const processedCourse = this.processCourseUrls(item)
      this.playingCourse = processedCourse
      console.log(`切换到课程: "${processedCourse.title}"`)

      // 滚动到当前item
      this.$nextTick(() => {
        const itemRef = this.$refs[`videoItem-${index}`]
        if (itemRef) {
          const itemview = Array.isArray(itemRef) ? itemRef[0] : itemRef
          itemview.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start'
          })
        }
      })

      // 开始新视频的播放统计
      this.startVideoPlayTracking()
    },

    // 新增：视频播放结束处理
    onVideoEnded () {
      console.log('🎬 视频播放结束:', this.playingCourse?.title)

      // 增加当前视频循环计数
      this.currentVideoLoopCount++
      console.log(
        `🔄 视频循环计数增加: ${this.currentVideoLoopCount}/${this.currentVideoRequiredCount}`
      )

      // 记录视频播放时长
      this.recordCurrentVideoPlayTime('视频播放结束')

      // 判断当前视频是否已完成所需的循环次数
      if (this.currentVideoLoopCount >= this.currentVideoRequiredCount) {
        console.log(
          `✅ 当前视频动作已完成 ${this.currentVideoRequiredCount} 次，准备切换到下一个动作`
        )

        // 标记当前视频为已完成
        this.markCurrentVideoAsCompleted('视频循环次数完成')

        // 显示休息弹窗（除非是最后一个视频）
        if (this.hasNextVideo()) {
          this.showActionCompleteDialog()
        } else {
          // 最后一个视频，直接切换
          this.playNextVideo()
        }
      } else {
        // 还没完成所需次数，显示循环间隔休息弹窗
        console.log(
          `🔁 准备继续播放当前视频 (${this.currentVideoLoopCount}/${this.currentVideoRequiredCount})`
        )
        this.showLoopRestForContinue()
      }
    },

    // 新增：视频时间更新处理
    onVideoTimeUpdate (event) {
      const video = event.target
      const currentTime = video.currentTime
      const duration = video.duration

      // 更新当前视频信息
      this.currentVideoInfo.currentTime = currentTime
      this.currentVideoInfo.duration = duration

      // 检查是否接近结尾（95%以上）且尚未标记为完成
      if (duration > 0 && currentTime > 0) {
        const progress = currentTime / duration
        const courseId = this.playingCourse?.courseId

        if (
          progress >= 0.95 &&
          courseId &&
          courseId === this.currentVideoInfo.courseId &&
          !this.currentVideoInfo.hasBeenMarkedAsCompleted
        ) {
          console.log(
            `视频播放进度达到 ${Math.round(progress * 100)}%，标记为完成`
          )
          this.markCurrentVideoAsCompleted('播放进度达到95%')
        }
      }
    },

    // 新增：视频元数据加载完成
    onVideoLoadedMetadata (event) {
      const video = event.target
      const courseId = this.playingCourse?.courseId

      // 重置当前视频信息
      this.currentVideoInfo = {
        courseId: courseId,
        duration: video.duration,
        currentTime: 0,
        hasBeenMarkedAsCompleted: false,
        playStartTime: Date.now()
      }

      console.log(
        `视频元数据加载完成: "${this.playingCourse?.title}" (时长: ${Math.round(
          video.duration
        )}秒)`
      )

      // 开始视频播放统计
      this.startVideoPlayTracking()
    },

    // 新增：视频暂停事件处理
    onVideoPause (event) {
      console.log('⏸️ 视频已暂停 - 同时暂停倒计时')

      // 暂停训练计时器（暂停倒计时）
      this.pauseTrainingTimer()

      // 记录当前视频播放时长
      this.recordCurrentVideoPlayTime('视频暂停')
    },

    // 新增：视频播放事件处理
    onVideoPlay (event) {
      console.log('▶️ 视频开始播放 - 同时恢复倒计时')

      // 恢复训练计时器（恢复倒计时）
      this.resumeTrainingTimer()

      // 重新开始视频播放统计
      this.currentVideoInfo.playStartTime = Date.now()
    },

    // 新增：开始视频播放统计追踪
    startVideoPlayTracking () {
      if (!this.playingCourse || !this.playingCourse.courseId) {
        return
      }

      const courseId = this.playingCourse.courseId
      const courseTitle = this.playingCourse.title

      // 初始化视频统计数据
      if (!this.videoPlayStats[courseId]) {
        this.videoPlayStats[courseId] = {
          courseId: courseId,
          title: courseTitle,
          playCount: 0,
          totalPlayTime: 0, // 总播放时长（毫秒）
          sessions: [] // 播放会话记录
        }
      }

      // 增加播放次数
      this.videoPlayStats[courseId].playCount++

      // 记录播放开始时间
      this.currentVideoInfo.playStartTime = Date.now()

      console.log(
        `📊 开始追踪视频播放: "${courseTitle}" (第${this.videoPlayStats[courseId].playCount}次播放)`
      )
    },

    // 新增：记录当前视频播放时长
    recordCurrentVideoPlayTime (reason = '记录播放时长') {
      if (!this.currentVideoInfo.playStartTime || !this.playingCourse) {
        return
      }

      const courseId = this.playingCourse.courseId
      const playEndTime = Date.now()
      const sessionDuration = playEndTime - this.currentVideoInfo.playStartTime

      // 更新统计数据
      if (this.videoPlayStats[courseId] && sessionDuration > 0) {
        this.videoPlayStats[courseId].totalPlayTime += sessionDuration

        // 记录播放会话
        this.videoPlayStats[courseId].sessions.push({
          startTime: new Date(
            this.currentVideoInfo.playStartTime
          ).toLocaleTimeString(),
          endTime: new Date(playEndTime).toLocaleTimeString(),
          duration: sessionDuration,
          reason: reason
        })

        console.log(
          `📊 记录播放时长: "${this.playingCourse.title}" - ${Math.round(
            sessionDuration / 1000
          )}秒 (原因: ${reason})`
        )
      }

      // 重置播放开始时间
      this.currentVideoInfo.playStartTime = null
    },

    // 新增：生成完整的运动统计数据
    generateExerciseStatistics () {
      // 记录当前正在播放的视频时长
      this.recordCurrentVideoPlayTime('运动结束')

      const statistics = {
        // 基础训练信息
        exerciseInfo: {
          trainId: this.planId,
          trainTitle: this.data?.plan?.title || '未知训练方案',
          startTime: this.trainingStartTime
            ? new Date(this.trainingStartTime).toLocaleString()
            : '未知',
          endTime: new Date().toLocaleString(),
          totalDuration: this.trainingDuration, // 总训练时长（秒）
          formattedDuration: this.formattedDuration
        },

        // 运动统计
        movementStats: {
          armCount: this.armCount,
          legCount: this.legCount,
          totalMovements: this.armCount + this.legCount
        },

        // 课程完成情况
        courseProgress: {
          totalCourses: this.totalCount,
          completedCourses: this.completedCount,
          completionRate:
            this.totalCount > 0
              ? Math.round((this.completedCount / this.totalCount) * 100)
              : 0,
          completedCourseIds: [...this.completedCourses]
        },

        // 视频播放详细统计
        videoPlayDetails: {},

        // 总播放统计
        playbackSummary: {
          totalVideos: 0,
          totalPlayCount: 0,
          totalPlayTime: 0, // 毫秒
          formattedTotalPlayTime: '',
          averagePlayTime: 0, // 毫秒
          formattedAveragePlayTime: ''
        }
      }

      // 处理视频播放统计
      let totalPlayCount = 0
      let totalPlayTime = 0

      Object.keys(this.videoPlayStats).forEach((courseId) => {
        const stats = this.videoPlayStats[courseId]

        statistics.videoPlayDetails[courseId] = {
          courseId: stats.courseId,
          videoName: stats.title,
          playCount: stats.playCount,
          totalPlayTime: stats.totalPlayTime, // 毫秒
          formattedPlayTime: this.formatMilliseconds(stats.totalPlayTime),
          averageSessionTime:
            stats.playCount > 0
              ? Math.round(stats.totalPlayTime / stats.playCount)
              : 0,
          formattedAverageSessionTime:
            stats.playCount > 0
              ? this.formatMilliseconds(
                Math.round(stats.totalPlayTime / stats.playCount)
              )
              : '0秒',
          isCompleted: this.completedCourses.includes(courseId),
          sessions: [...stats.sessions]
        }

        totalPlayCount += stats.playCount
        totalPlayTime += stats.totalPlayTime
      })

      // 更新总播放统计
      statistics.playbackSummary.totalVideos = Object.keys(
        this.videoPlayStats
      ).length
      statistics.playbackSummary.totalPlayCount = totalPlayCount
      statistics.playbackSummary.totalPlayTime = totalPlayTime
      statistics.playbackSummary.formattedTotalPlayTime =
        this.formatMilliseconds(totalPlayTime)
      statistics.playbackSummary.averagePlayTime =
        totalPlayCount > 0 ? Math.round(totalPlayTime / totalPlayCount) : 0
      statistics.playbackSummary.formattedAveragePlayTime =
        totalPlayCount > 0
          ? this.formatMilliseconds(Math.round(totalPlayTime / totalPlayCount))
          : '0秒'

      return statistics
    },

    // 新增：格式化毫秒为可读时间
    formatMilliseconds (milliseconds) {
      const seconds = Math.floor(milliseconds / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)

      if (hours > 0) {
        return `${hours}小时${minutes % 60}分${seconds % 60}秒`
      } else if (minutes > 0) {
        return `${minutes}分${seconds % 60}秒`
      } else {
        return `${seconds}秒`
      }
    },

    // 新增：标记当前视频为已完成（抽取公共逻辑）
    markCurrentVideoAsCompleted (reason = '未知原因') {
      console.log('🏷️ 开始标记视频为完成:', reason)
      console.log('🏷️ 检查播放课程:', {
        有播放课程: !!this.playingCourse,
        课程ID: this.playingCourse?.courseId,
        课程标题: this.playingCourse?.title
      })

      if (!this.playingCourse || !this.playingCourse.courseId) {
        console.log('❌ 无法标记：没有有效的播放课程')
        return false
      }

      const courseId = this.playingCourse.courseId
      console.log('🏷️ 要标记的课程ID:', courseId)
      console.log('🏷️ 当前已完成课程列表:', this.completedCourses)

      // 检查是否已经标记过
      if (this.completedCourses.includes(courseId)) {
        console.log(
          `⚠️ 课程 "${this.playingCourse.title}" 已经标记为完成，跳过重复标记`
        )
        return false
      }

      // 标记为已完成
      console.log('✅ 正在添加到完成列表...')
      this.completedCourses.push(courseId)
      this.currentVideoInfo.hasBeenMarkedAsCompleted = true

      console.log('✅ 添加后的完成列表:', this.completedCourses)
      console.log('📊 重新计算统计数据...')
      console.log('📊 completedCount:', this.completedCount)
      console.log('📊 totalCount:', this.totalCount)

      // 强制触发视图更新
      this.$forceUpdate()

      this.updateProgressBars()

      console.log(
        `✅ 课程 "${this.playingCourse.title}" 已标记为完成 (原因: ${reason})`
      )
      console.log(`📊 当前进度: ${this.completedCount}/${this.totalCount}`)

      // 关键操作：课程完成，触发保存
      this.saveOnCriticalAction('课程完成')

      return true
    },

    // 新增：播放下一个视频
    playNextVideo () {
      if (!this.courses || this.courses.length === 0) {
        console.log('没有可播放的课程')
        return
      }

      // 找到当前播放视频的索引
      const currentIndex = this.courses.findIndex(
        (course) => course.courseId === this.playingCourse?.courseId
      )

      // 计算下一个视频的索引
      const nextIndex = currentIndex + 1

      if (nextIndex < this.courses.length) {
        // 还有下一个视频，自动切换（不需要标记为完成，因为在onVideoEnded中已经处理了）
        const nextCourse = this.courses[nextIndex]
        console.log(`自动切换到下一个视频: "${nextCourse.title}"`)
        this.switchToCourse(nextCourse, nextIndex, false)
      } else {
        // 已经是最后一个视频
        console.log('所有视频播放完成！')
        this.onAllVideosCompleted()
      }
    },

    // 新增：所有视频播放完成处理
    onAllVideosCompleted () {
      console.log('🎉 恭喜！所有训练视频播放完成')

      // 清理保存的训练状态（因为训练全部完成）
      this.clearSavedTrainingState()

      // 确保最后的进度更新
      this.updateProgressBars()

      // // 生成并打印完整的运动统计数据
      // const exerciseStats = this.generateExerciseStatistics()
      // console.log('🏆 ===== 全部视频完成 - 运动训练完整统计数据 =====')
      // console.log(exerciseStats)
      // console.log('📊 视频播放详细统计:')
      // console.table(exerciseStats.videoPlayDetails)
      // console.log('📈 播放汇总统计:')
      // console.table(exerciseStats.playbackSummary)
      // console.log('🎯 课程完成统计:')
      // console.table(exerciseStats.courseProgress)
      // console.log('💪 运动次数统计:')
      // console.table(exerciseStats.movementStats)
      // console.log('⏰ 训练基础信息:')
      // console.table(exerciseStats.exerciseInfo)
      // console.log('🏆 ===== 全部视频完成统计数据结束 =====')

      // // 生成用于后台存储的JSON格式训练记录
      // const trainingRecordJson = this.generateTrainingRecordJson(exerciseStats)
      // console.log('💾 ===== 全部视频完成 - 用于后台存储的训练记录JSON =====')
      // console.log('JSON格式训练记录:')
      // console.log(trainingRecordJson)
      // console.log('JSON字符串 (可直接发送给后台):')
      // console.log(JSON.stringify(JSON.parse(trainingRecordJson), null, 2))
      // console.log('💾 ===== 全部视频完成训练记录JSON结束 =====')

      // // 将JSON数据存储到组件实例，方便外部调用
      // this.finalTrainingRecordJson = trainingRecordJson

      // // 可以在这里添加完成提示、统计总结等功能
      // console.log(`训练总结:
      //   - 总训练时长: ${this.formattedDuration}
      //   - 完成动作数: ${this.completedCount}/${this.totalCount}
      //   - 手臂运动次数: ${this.armCount}
      //   - 腿部运动次数: ${this.legCount}
      // `)

      // // 可以弹出完成提示
      // // if (this.$toast) {
      // //   this.$toast.success('🎉 恭喜完成所有训练课程！')
      // // }

      // // 或者可以添加重新开始训练的选项
      // // setTimeout(() => {
      // //   if (confirm('训练完成！是否重新开始？')) {
      // //     this.resetTrainingProgress()
      // //   }
      // // }, 2000)
    },

    // 新增：重置训练进度（可选功能）
    resetTrainingProgress () {
      this.completedCourses = []
      this.armCount = 0
      this.legCount = 0
      this.trainingDuration = 0
      this.trainingStartTime = Date.now()

      // 确保第一个课程的URL使用HTTPS协议
      const firstCourse = this.courses[0] || null
      this.playingCourse = firstCourse
        ? this.processCourseUrls(firstCourse)
        : null

      // 重置运动状态（避免计数异常）
      this.resetMovementCounts()

      this.updateProgressBars()
      console.log('训练进度已重置')
    },

    // 新增：调试方法 - 显示当前训练统计
    showTrainingStats () {
      console.log('📊 当前训练统计:', {
        总课程数: this.totalCount,
        已完成课程数: this.completedCount,
        完成进度: `${this.trainingProgress}%`,
        已完成课程列表: this.completedCourses,
        当前播放课程: this.playingCourse?.courseId,
        当前视频信息: this.currentVideoInfo,
        训练时长: this.formattedDuration,
        手臂运动次数: this.armCount,
        腿部运动次数: this.legCount,
        视频播放统计: this.videoPlayStats
      })

      // 显示详细的视频播放统计
      if (Object.keys(this.videoPlayStats).length > 0) {
        console.log('🎬 详细视频播放统计:')
        console.table(this.videoPlayStats)
      }

      return {
        总课程数: this.totalCount,
        已完成课程数: this.completedCount,
        完成进度: `${this.trainingProgress}%`,
        已完成课程列表: this.completedCourses,
        视频播放统计: this.videoPlayStats
      }
    },

    // 新增：实时获取当前统计数据（不打印）
    getCurrentStats () {
      return this.generateExerciseStatistics()
    },

    // 新增：生成用于后台存储的训练记录JSON
    generateTrainingRecordJson (exerciseStats) {
      const trainingRecord = {
        // 基础信息
        recordId: this.generateRecordId(), // 生成唯一记录ID
        userId: this.phone || 'unknown', // 用户手机号
        planId: this.planId || '', // 方案ID
        recordType: 'AI_EXERCISE_TRAINING', // 记录类型

        // 训练基础信息
        training: {
          planTitle: exerciseStats.exerciseInfo.trainTitle,
          startTime: exerciseStats.exerciseInfo.startTime,
          endTime: exerciseStats.exerciseInfo.endTime,
          totalDurationSeconds: exerciseStats.exerciseInfo.totalDuration,
          formattedDuration: exerciseStats.exerciseInfo.formattedDuration,
          isCompleted: exerciseStats.courseProgress.completionRate >= 100
        },

        // 课程完成统计
        courseCompletion: {
          totalCourses: exerciseStats.courseProgress.totalCourses,
          completedCourses: exerciseStats.courseProgress.completedCourses,
          completionRate: exerciseStats.courseProgress.completionRate,
          completedCourseIds: exerciseStats.courseProgress.completedCourseIds
        },

        // 运动统计
        movements: {
          armCount: exerciseStats.movementStats.armCount,
          legCount: exerciseStats.movementStats.legCount,
          totalMovements: exerciseStats.movementStats.totalMovements
        },

        // 视频播放详细统计
        videoPlayback: {
          summary: {
            totalVideos: exerciseStats.playbackSummary.totalVideos,
            totalPlayCount: exerciseStats.playbackSummary.totalPlayCount,
            totalPlayTimeMs: exerciseStats.playbackSummary.totalPlayTime,
            formattedTotalPlayTime:
              exerciseStats.playbackSummary.formattedTotalPlayTime,
            averagePlayTimeMs: exerciseStats.playbackSummary.averagePlayTime,
            formattedAveragePlayTime:
              exerciseStats.playbackSummary.formattedAveragePlayTime
          },
          details: this.formatVideoDetailsForBackend(
            exerciseStats.videoPlayDetails
          )
        },

        // 运动评价
        evaluation: {
          rating: this.exerciseRating || 0,
          ratingText: this.getRatingText() || '',
          encouragementText: this.encouragementText || ''
        },

        // 系统信息
        systemInfo: {
          deviceType: this.isApp ? 'APP' : 'WEB',
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
          version: '1.0.0'
        }
      }

      return JSON.stringify(trainingRecord, null, 2)
    },

    // 新增：格式化视频详情数据用于后台存储
    formatVideoDetailsForBackend (videoPlayDetails) {
      const formattedDetails = []

      Object.keys(videoPlayDetails).forEach((courseId) => {
        const video = videoPlayDetails[courseId]
        formattedDetails.push({
          courseId: video.courseId,
          videoName: video.videoName,
          playCount: video.playCount,
          totalPlayTimeMs: video.totalPlayTime,
          formattedPlayTime: video.formattedPlayTime,
          averageSessionTimeMs: video.averageSessionTime,
          formattedAverageSessionTime: video.formattedAverageSessionTime,
          isCompleted: video.isCompleted,
          playSessions: video.sessions.map((session) => ({
            startTime: session.startTime,
            endTime: session.endTime,
            durationMs: session.duration,
            formattedDuration: this.formatMilliseconds(session.duration),
            reason: session.reason
          }))
        })
      })

      return formattedDetails
    },

    // 新增：生成唯一记录ID
    generateRecordId () {
      const timestamp = Date.now()
      const random = Math.random().toString(36).substr(2, 9)
      return `TR_${timestamp}_${random}`
    },

    // 新增：获取训练记录JSON（供外部调用）
    getTrainingRecordJson () {
      if (this.finalTrainingRecordJson) {
        return this.finalTrainingRecordJson
      }

      // 如果还没有生成最终记录，则实时生成
      const exerciseStats = this.generateExerciseStatistics()
      return this.generateTrainingRecordJson(exerciseStats)
    },

    // 新增：将训练记录发送到后台（示例方法）
    async submitTrainingRecord () {
      try {
        const recordJson = this.getTrainingRecordJson()
        const recordData = JSON.parse(recordJson)

        console.log('📤 准备发送训练记录到后台:', recordData)

        // 这里可以调用实际的API接口
        // const response = await fetch('/api/training-records', {
        //   method: 'POST',
        //   headers: {
        //     'Content-Type': 'application/json',
        //   },
        //   body: recordJson
        // })

        // 示例：打印到控制台
        console.log('✅ 训练记录提交成功 (模拟)')
        console.log('📋 提交的数据:', recordData)

        return {
          success: true,
          recordId: recordData.recordId,
          message: '训练记录保存成功'
        }
      } catch (error) {
        console.error('❌ 提交训练记录失败:', error)
        return {
          success: false,
          error: error.message,
          message: '训练记录保存失败'
        }
      }
    },

    // 新增：提交训练记录到服务器
    async submitTrainingRecordToServer (trainingRecordJson) {
      try {
        if (!this.phone) {
          console.warn('⚠️ 没有用户手机号，跳过提交训练记录')
          return
        }

        console.log('📤 开始提交训练记录到服务器...')
        console.log('📱 用户手机号:', this.phone)
        console.log('📋 训练记录JSON长度:', trainingRecordJson.length)

        // 调用 DakaDao.addAiPad 接口
        const result = await DakaDao.addAiPad(this.phone, trainingRecordJson)

        if (result) {
          console.log('✅ 训练记录提交成功:', result)
          // 可以在这里添加成功提示
          // this.$toast?.success('训练记录保存成功')
        } else {
          console.error('❌ 训练记录提交失败: 接口返回空数据')
          // 可以在这里添加失败提示
          // this.$toast?.error('训练记录保存失败，请稍后重试')
        }
      } catch (error) {
        console.error('❌ 提交训练记录时发生错误:', error)
        // 可以在这里添加错误提示
        // this.$toast?.error('网络错误，训练记录保存失败')
      }
    },
    // 新增：格式化视频时长
    formatDuration (duration) {
      const minutes = Math.floor(duration / 60000)
      const seconds = ((duration % 60000) / 1000).toFixed(0)
      if (minutes === 0) {
        return `${seconds}秒`
      }
      return `${minutes}分${seconds.padStart(2, '0')}秒`
    },
    // 新增：获取动作训练要求
    getActionRequirement (courseId) {
      if (!this.data || !this.data.plan || !this.data.plan.planCourseBeans) {
        return '暂无训练要求'
      }

      // 查找courseId对应的数据
      const course = this.data.plan.planCourseBeans.find(
        (c) => c.courseId === courseId
      )

      // 如果找到数据，则返回格式化文本
      if (course) {
        if (course.groups === 0) {
          return '无要求'
        }
        return `${course.groups}${course.groupsUnit}，每次做${course.round}组，组间适当休息${course.rest}秒或根据个人情况调整。`
      } else {
        return '未找到该课程训练要求'
      }
    },
    // 新增：更新进度条
    updateProgressBars () {
      this.$nextTick(() => {
        // 计算进度百分比
        const armProgress = Math.min((this.armCount / 100) * 100, 100) // 假设目标是100次
        const legProgress = Math.min((this.legCount / 100) * 100, 100) // 假设目标是100次
        const timeProgress = Math.min((this.trainingDuration / 1800) * 100, 100) // 假设目标是30分钟
        const courseProgress = this.trainingProgress // 使用computed属性

        // 更新进度条宽度
        const timeProgressBar = document.querySelector('.time-progress')
        const courseProgressBar = document.querySelector('.course-progress')
        const armProgressBar = document.querySelector('.arm-progress')
        const legProgressBar = document.querySelector('.leg-progress')

        if (timeProgressBar) {
          timeProgressBar.style.width = `${timeProgress}%`
        }
        if (courseProgressBar) {
          courseProgressBar.style.width = `${courseProgress}%`
        }
        if (armProgressBar) {
          armProgressBar.style.width = `${armProgress}%`
        }
        if (legProgressBar) {
          legProgressBar.style.width = `${legProgress}%`
        }
      })
    },

    // 新增：开始训练计时
    startTrainingTimer (isResume = false) {
      if (!isResume) {
        // 正常开始训练，设置新的开始时间
        this.trainingStartTime = Date.now()
        this.trainingDuration = 0

        // 初始化倒计时时间（从方案的总时长开始）
        if (this.isTestMode) {
          this.countdownRemaining = 10 // 测试模式：10秒
          console.log('🧪 测试模式倒计时: 10秒')
        } else if (this.data && this.data.plan && this.data.plan.exerciseTime) {
          this.countdownRemaining = this.data.plan.exerciseTime * 60 // 将分钟转换为秒
          console.log(
            `⏰ 初始化倒计时: ${this.data.plan.exerciseTime}分钟 (${this.countdownRemaining}秒)`
          )
        }
      } else {
        // 恢复训练，调整开始时间以保持已有的训练时长
        const savedDuration = this.trainingDuration
        this.trainingStartTime = Date.now() - savedDuration * 1000
        console.log(`🔄 恢复训练计时器，已有训练时长: ${savedDuration}秒`)

        // 恢复倒计时剩余时间
        if (this.isTestMode) {
          this.countdownRemaining = Math.max(0, 10 - savedDuration)
          console.log(
            '🧪 测试模式恢复倒计时剩余时间:',
            this.countdownRemaining,
            '秒'
          )
        } else if (this.data && this.data.plan && this.data.plan.exerciseTime) {
          const totalSeconds = this.data.plan.exerciseTime * 60
          this.countdownRemaining = Math.max(0, totalSeconds - savedDuration)
          console.log(`⏰ 恢复倒计时剩余时间: ${this.countdownRemaining}秒`)
        }
      }

      this.isTrainingActive = true // 标记训练为活跃状态
      this.isAutoEnding = false // 重置自动结束标志
      this.isTimerPaused = false // 重置暂停状态
      this.pausedTime = 0 // 重置暂停时的时长

      // 如果是新开始训练，重置当前视频循环计数
      if (!isResume) {
        this.currentVideoLoopCount = 0
        console.log('🔄 新开始训练，重置视频循环计数为 0')
      }
      this.durationTimer = setInterval(() => {
        this.trainingDuration = Math.floor(
          (Date.now() - this.trainingStartTime) / 1000
        )

        // 更新倒计时剩余时间
        let totalSeconds = 0
        if (this.isTestMode) {
          totalSeconds = 10 // 测试模式：10秒
        } else if (this.data && this.data.plan && this.data.plan.exerciseTime) {
          totalSeconds = this.data.plan.exerciseTime * 60
        }

        if (totalSeconds > 0) {
          this.countdownRemaining = Math.max(
            0,
            totalSeconds - this.trainingDuration
          )

          // 检查倒计时是否结束
          if (this.countdownRemaining <= 0) {
            console.log('⏰ 倒计时结束，自动执行结束运动逻辑')
            this.autoEndExercise()
            return
          }
        }

        this.updateProgressBars()
      }, 1000)
      console.log(
        `🏃‍♂️ 训练计时器已启动，训练状态设置为活跃 (${isResume ? '恢复模式' : '新开始'
        })`
      )
    },

    // 新增：停止训练计时
    stopTrainingTimer () {
      if (this.durationTimer) {
        clearInterval(this.durationTimer)
        this.durationTimer = null
      }
      this.isTrainingActive = false // 标记训练为非活跃状态
      this.isAutoEnding = false // 重置自动结束标志
      this.isTimerPaused = false // 重置暂停状态
      this.pausedTime = 0 // 重置暂停时的时长

      // 停止自动保存
      this.stopAutoSave()

      console.log('⏹️ 训练计时器已停止，训练状态设置为非活跃')
    },

    // 新增：暂停训练计时
    pauseTrainingTimer () {
      if (!this.isTrainingActive || this.isTimerPaused) {
        console.log('⚠️ 计时器未运行或已暂停，无需暂停')
        return
      }

      // 记录暂停时的时长
      this.pausedTime = this.trainingDuration

      // 停止计时器但保持训练活跃状态
      if (this.durationTimer) {
        clearInterval(this.durationTimer)
        this.durationTimer = null
      }

      this.isTimerPaused = true
      console.log(`⏸️ 训练计时器已暂停，当前时长: ${this.pausedTime}秒`)
    },

    // 新增：恢复训练计时
    resumeTrainingTimer () {
      if (!this.isTrainingActive || !this.isTimerPaused) {
        console.log('⚠️ 计时器未暂停或训练未活跃，无需恢复')
        return
      }

      // 调整开始时间，使得继续计时时保持之前的时长
      this.trainingStartTime = Date.now() - this.pausedTime * 1000

      // 重新启动计时器
      this.durationTimer = setInterval(() => {
        this.trainingDuration = Math.floor(
          (Date.now() - this.trainingStartTime) / 1000
        )

        // 更新倒计时剩余时间
        let totalSeconds = 0
        if (this.isTestMode) {
          totalSeconds = 10 // 测试模式：10秒
        } else if (this.data && this.data.plan && this.data.plan.exerciseTime) {
          totalSeconds = this.data.plan.exerciseTime * 60
        }

        if (totalSeconds > 0) {
          this.countdownRemaining = Math.max(
            0,
            totalSeconds - this.trainingDuration
          )

          // 检查倒计时是否结束
          if (this.countdownRemaining <= 0) {
            console.log('⏰ 倒计时结束，自动执行结束运动逻辑')
            this.autoEndExercise()
            return
          }
        }

        this.updateProgressBars()
      }, 1000)

      this.isTimerPaused = false
      console.log(`▶️ 训练计时器已恢复，从${this.pausedTime}秒继续计时`)
    },

    showEndConfirm () {
      console.log('显示结束运动确认弹窗')
      // 停止计时器
      this.stopTrainingTimer()
      // 停止姿态检测
      this.stopDetection()
      // 暂停当前播放的视频
      const video = this.$refs.mainVideo
      if (video && !video.paused) {
        video.pause()
        console.log('🔇 视频已暂停 - 用户点击结束运动按钮')
      }

      this.showEndConfirmDialog = true
    },

    cancelEndExercise () {
      console.log('取消结束运动，恢复训练')
      // 恢复计时器（使用resume模式）
      this.startTrainingTimer(true)
      // 恢复姿态检测
      this.startDetection()
      // 恢复视频播放
      const video = this.$refs.mainVideo
      if (video && video.paused) {
        video.play().catch((error) => {
          console.warn('恢复视频播放失败:', error)
        })
        console.log('▶️ 视频已恢复播放 - 用户取消结束运动')
      }

      this.showEndConfirmDialog = false
    },

    // 新增：倒计时结束自动执行结束运动
    autoEndExercise () {
      // 防止重复调用
      if (this.isAutoEnding) {
        console.log('⚠️ 自动结束已在进行中，跳过重复调用')
        return
      }

      this.isAutoEnding = true
      console.log('⏰ 倒计时结束，自动结束运动训练')

      // 停止计时器（这个在confirmEndExercise中也会调用，但为了保险起见先调用）
      this.stopTrainingTimer()

      // 停止姿态检测
      this.stopDetection()

      // 暂停当前播放的视频
      const video = this.$refs.mainVideo
      if (video && !video.paused) {
        video.pause()
        console.log('🔇 视频已暂停 - 倒计时自动结束')
      }

      // 直接调用确认结束运动的逻辑，但不显示确认弹窗
      this.confirmEndExercise()
    },

    confirmEndExercise () {
      this.showEndConfirmDialog = false

      // 停止训练计时
      this.stopTrainingTimer()
      // 停止姿态检测 (确保在结束时停止)
      this.stopDetection()

      // 清理保存的训练状态（因为训练正常结束）
      this.clearSavedTrainingState()

      // 生成并打印完整的运动统计数据
      const exerciseStats = this.generateExerciseStatistics()
      console.log('🏆 ===== 运动训练完整统计数据 =====')
      console.log(exerciseStats)
      console.log('📊 视频播放详细统计:')
      console.table(exerciseStats.videoPlayDetails)
      console.log('📈 播放汇总统计:')
      console.table(exerciseStats.playbackSummary)
      console.log('🎯 课程完成统计:')
      console.table(exerciseStats.courseProgress)
      console.log('💪 运动次数统计:')
      console.table(exerciseStats.movementStats)
      console.log('⏰ 训练基础信息:')
      console.table(exerciseStats.exerciseInfo)
      console.log('🏆 ===== 统计数据结束 =====')

      // 生成用于后台存储的JSON格式训练记录
      const trainingRecordJson = this.generateTrainingRecordJson(exerciseStats)
      console.log('💾 ===== 用于后台存储的训练记录JSON =====')
      console.log('JSON格式训练记录:')
      console.log(trainingRecordJson)
      console.log('JSON字符串 (可直接发送给后台):')
      console.log(JSON.stringify(JSON.parse(trainingRecordJson), null, 2))
      console.log('💾 ===== 训练记录JSON结束 =====')

      // 将JSON数据存储到组件实例，方便外部调用
      this.finalTrainingRecordJson = trainingRecordJson

      // 调用接口保存训练记录
      this.submitTrainingRecordToServer(trainingRecordJson)

      // 计算运动评价
      this.calculateExerciseRating()

      // 生成鼓励词
      this.generateEncouragementText()

      // 重置结束统计视频状态
      this.endStatsVideoPlayCount = 0
      this.isEndStatsVideoMuted = false // 默认开启声音

      // 显示统计弹框
      this.showEndStatsDialog = true

      // 启动结束统计数字人视频播放
      this.startEndStatsVideo()

      // 播放完成音频
      this.playCompletedSound()

      console.log('💪 运动训练结束，显示统计数据')
    },

    // 新增：计算运动评价（1-5星）
    calculateExerciseRating () {
      let rating = 1 // 基础分

      // 根据训练时长评分 (30分钟以上5分，20分钟以上4分，10分钟以上3分，5分钟以上2分)
      if (this.trainingDuration >= 1800) {
        // 30分钟
        rating = 5
      } else if (this.trainingDuration >= 1200) {
        // 20分钟
        rating = 4
      } else if (this.trainingDuration >= 600) {
        // 10分钟
        rating = 3
      } else if (this.trainingDuration >= 300) {
        // 5分钟
        rating = 2
      }

      // 根据完成课程比例调整评分
      const completionRate =
        this.totalCount > 0 ? this.completedCount / this.totalCount : 0
      if (completionRate >= 0.8) {
        rating = Math.min(5, rating + 1) // 完成80%以上加1分
      } else if (completionRate >= 0.5) {
        // 完成50%以上保持评分
      } else {
        rating = Math.max(1, rating - 1) // 完成50%以下减1分
      }

      // 根据运动次数调整评分
      const totalMovements = this.armCount + this.legCount
      if (totalMovements >= 100) {
        rating = Math.min(5, rating + 1) // 运动次数多加1分
      } else if (totalMovements < 20) {
        rating = Math.max(1, rating - 1) // 运动次数少减1分
      }

      this.exerciseRating = Math.max(1, Math.min(5, rating))

      console.log('⭐ 运动评价计算:', {
        训练时长: this.trainingDuration,
        完成率: completionRate,
        运动次数: totalMovements,
        最终评分: this.exerciseRating
      })
    },

    // 新增：生成鼓励词
    generateEncouragementText () {
      let category = 'normal'

      // 根据评分选择鼓励词类别
      if (this.exerciseRating >= 5) {
        category = 'excellent'
      } else if (this.exerciseRating >= 4) {
        category = 'good'
      } else if (this.exerciseRating >= 3) {
        category = 'normal'
      } else {
        category = 'beginner'
      }

      // 随机选择一条鼓励词
      const texts = this.encouragementTexts[category]
      const randomIndex = Math.floor(Math.random() * texts.length)
      this.encouragementText = texts[randomIndex]

      console.log('🎉 生成鼓励词:', {
        评分: this.exerciseRating,
        类别: category,
        鼓励词: this.encouragementText
      })
    },

    // 新增：重新开始运动
    restartExercise () {
      console.log('🔄 重新开始运动训练')

      // 重置训练进度
      this.resetTrainingProgress()

      // 关闭弹框
      this.showEndStatsDialog = false

      // 重置结束统计视频状态
      this.endStatsVideoPlayCount = 0
      this.isEndStatsVideoMuted = false

      // 确保第一个课程已设置但不自动播放
      if (this.courses.length > 0) {
        // 确保第一个课程的URL使用HTTPS协议
        this.playingCourse = this.processCourseUrls(this.courses[0])
      }

      // 显示准备弹窗，由用户决定何时开始
      this.showPrepareForExercise()
    },

    // 新增：退出运动训练
    exitExercise () {
      console.log('🏠 退出运动训练')

      // 停止所有计时器
      this.stopTrainingTimer()

      // 清理摄像头资源
      this.cleanup()

      // 关闭弹框
      this.showEndStatsDialog = false

      // 重置结束统计视频状态
      this.endStatsVideoPlayCount = 0
      this.isEndStatsVideoMuted = false

      // 允许浏览器返回
      this.allowBrowserBack()

      // 这里可以添加页面跳转逻辑
      // 例如：this.$router.go(-1) 或 this.$router.push('/某个页面')
      // if (this.isApp) {
      //   // 如果是App环境，可以调用App的返回方法
      //   console.log('App环境，调用返回方法')
      // } else {
      //   // 浏览器环境，返回上一页
      //   window.history.back()
      // }
    },

    // 新增：获取评价文本
    getRatingText () {
      const ratingTexts = {
        5: '完美表现！你是运动明星！',
        4: '优秀表现！继续保持！',
        3: '良好表现！稳步提升！',
        2: '不错的开始！继续努力！',
        1: '勇敢的第一步！坚持就是胜利！'
      }

      return ratingTexts[this.exerciseRating] || '继续加油！'
    },

    // 新增：调试相关方法
    toggleDebugMode () {
      this.debugMode = !this.debugMode
      console.log(`🔧 调试模式已${this.debugMode ? '开启' : '关闭'}`)
    },

    // 新增：调整人物跟踪阈值
    setTrackingThreshold (threshold) {
      this.personTrackingThreshold = Math.max(50, Math.min(300, threshold))
      console.log(
        `🎯 人物跟踪阈值已设置为: ${this.personTrackingThreshold}像素`
      )
    },

    // 新增：重置人物跟踪
    resetPersonTracking () {
      this.lastSelectedPersonCenter = null
      this.noPersonFrameCount = 0
      console.log('🔄 人物跟踪信息已重置')
    },

    // 新增：设置浏览器返回控制
    setupBrowserBackControl () {
      if (this.hasAddedUnloadListener) {
        console.log('⚠️ 浏览器返回控制监听器已存在，跳过重复添加')
        return
      }

      // 添加多种页面关闭检测事件
      window.addEventListener('beforeunload', this.handleBeforeUnload)
      window.addEventListener('unload', this.handleUnload)
      window.addEventListener('pagehide', this.handlePageHide)
      document.addEventListener('visibilitychange', this.handleVisibilityChange)

      // 添加 popstate 事件监听器（浏览器后退按钮）
      window.addEventListener('popstate', this.handlePopState)

      // 在history中添加一个状态，用于拦截返回操作
      const currentState = window.history.state
      if (!currentState || !currentState.preventBack) {
        window.history.pushState(
          { preventBack: true, timestamp: Date.now() },
          '',
          window.location.href
        )
      }

      this.hasAddedUnloadListener = true
      console.log('🔒 浏览器返回控制和多重保存监听器已设置')
    },

    // 新增：移除浏览器返回控制
    removeBrowserBackControl () {
      if (!this.hasAddedUnloadListener) {
        return
      }

      // 移除所有事件监听器
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
      window.removeEventListener('unload', this.handleUnload)
      window.removeEventListener('pagehide', this.handlePageHide)
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      )
      window.removeEventListener('popstate', this.handlePopState)

      this.hasAddedUnloadListener = false
      console.log('🔓 浏览器返回控制和多重保存监听器已移除')
    },

    // 新增：处理 beforeunload 事件
    handleBeforeUnload (event) {
      if (this.isTrainingActive && !this.showEndStatsDialog) {
        // 保存训练状态
        this.saveTrainingState('beforeunload事件')

        const message = '训练正在进行中，确定要离开吗？'
        event.preventDefault()
        event.returnValue = message
        return message
      }
    },

    // 新增：处理 unload 事件
    handleUnload (event) {
      console.log('🚪 unload事件触发')
      if (this.isTrainingActive && !this.showEndStatsDialog) {
        this.saveTrainingState('unload事件')
      }
    },

    // 新增：处理 pagehide 事件（移动端更可靠）
    handlePageHide (event) {
      console.log('📱 pagehide事件触发, persisted:', event.persisted)
      if (this.isTrainingActive && !this.showEndStatsDialog) {
        this.saveTrainingState('pagehide事件')
      }
    },

    // 新增：处理页面可见性变化（切换标签页、最小化等）
    handleVisibilityChange () {
      if (document.visibilityState === 'hidden') {
        console.log('👁️ 页面变为隐藏状态')
        if (this.isTrainingActive && !this.showEndStatsDialog) {
          this.saveTrainingState('页面隐藏')
        }
      } else if (document.visibilityState === 'visible') {
        console.log('👁️ 页面变为可见状态')
        // 页面重新可见时，可以考虑检查数据完整性
        this.checkDataIntegrity()
      }
    },

    // 新增：处理 popstate 事件（浏览器后退按钮）
    handlePopState (event) {
      console.log('🔙 检测到浏览器返回操作')
      console.log('🏃‍♂️ 当前训练状态:', {
        isTrainingActive: this.isTrainingActive,
        showEndStatsDialog: this.showEndStatsDialog,
        showEndConfirmDialog: this.showEndConfirmDialog
      })

      // 如果训练正在进行且没有显示结束统计弹框，则拦截返回
      if (
        this.isTrainingActive &&
        !this.showEndStatsDialog &&
        !this.showEndConfirmDialog
      ) {
        console.log('🛑 训练进行中，拦截返回操作并模拟点击结束运动按钮')

        // 阻止默认的返回行为，重新添加 history 状态
        window.history.pushState(
          { preventBack: true, timestamp: Date.now() },
          '',
          window.location.href
        )

        // 模拟点击结束运动按钮
        this.showEndConfirm()

        return false
      } else {
        console.log('✅ 训练已结束或正在显示结束弹框，允许返回')
        // 训练已结束，允许正常返回
        this.removeBrowserBackControl()
      }
    },

    // 新增：检查是否可以安全离开
    canSafelyLeave () {
      return !this.isTrainingActive || this.showEndStatsDialog
    },

    // 新增：强制允许返回（在确认结束后调用）
    allowBrowserBack () {
      console.log('🔓 允许浏览器返回')
      this.isTrainingActive = false
      this.removeBrowserBackControl()

      // 清理 history 状态并执行返回
      setTimeout(() => {
        if (this.isApp) {
          console.log('App环境，调用返回方法')
          // 如果是App环境，可以调用App的返回方法
        } else {
          console.log('浏览器环境，执行返回')
          window.history.back()
        }
      }, 100)
    },

    // 新增：保存训练状态到localStorage
    saveTrainingState (reason = '手动保存') {
      try {
        if (!this.isTrainingActive || !this.planId) {
          console.log('⚠️ 训练未活跃或无方案ID，跳过保存状态')
          return false
        }

        const now = Date.now()
        // 防止频繁保存（1秒内不重复保存，除非是关键事件）
        const criticalEvents = [
          'beforeunload事件',
          'unload事件',
          'pagehide事件'
        ]
        if (
          !criticalEvents.includes(reason) &&
          now - this.lastSaveTime < 1000
        ) {
          console.log('⚠️ 保存过于频繁，跳过本次保存')
          return false
        }

        // 记录当前正在播放的视频时长
        this.recordCurrentVideoPlayTime('保存训练状态')

        const trainingState = {
          // 基础信息
          phone: this.phone,
          planId: this.planId,
          saveTime: now,
          saveTimeFormatted: new Date().toLocaleString(),
          saveReason: reason,

          // 训练进度
          trainingStartTime: this.trainingStartTime,
          trainingDuration: this.trainingDuration,
          isTrainingActive: this.isTrainingActive,

          // 课程进度
          playingCourseId: this.playingCourse?.courseId,
          completedCourses: [...this.completedCourses],
          totalCourses: this.totalCount,

          // 运动统计
          armCount: this.armCount,
          legCount: this.legCount,

          // 视频播放统计
          videoPlayStats: JSON.parse(JSON.stringify(this.videoPlayStats)),
          currentVideoInfo: { ...this.currentVideoInfo },

          // 方案数据（避免重新加载）
          data: this.data,
          courses: this.courses.map((course) => ({ ...course }))
        }

        localStorage.setItem(
          this.TRAINING_STORAGE_KEY,
          JSON.stringify(trainingState)
        )
        this.lastSaveTime = now

        console.log(`💾 训练状态已保存到localStorage (${reason}):`, {
          保存时间: trainingState.saveTimeFormatted,
          训练时长: this.formattedDuration,
          完成课程: this.completedCount + '/' + this.totalCount,
          当前课程: this.playingCourse?.title
        })

        return true
      } catch (error) {
        console.error('❌ 保存训练状态失败:', error)
        return false
      }
    },

    // 新增：检查并加载上次保存的训练状态
    checkSavedTrainingState () {
      try {
        const savedData = localStorage.getItem(this.TRAINING_STORAGE_KEY)
        if (!savedData) {
          console.log('📝 没有找到保存的训练状态')
          return null
        }

        const trainingState = JSON.parse(savedData)
        console.log('📋 发现保存的训练状态:', {
          保存时间: trainingState.saveTimeFormatted,
          方案ID: trainingState.planId,
          用户: trainingState.phone,
          训练时长: this.formatDuration(trainingState.trainingDuration * 1000),
          完成课程:
            trainingState.completedCourses.length +
            '/' +
            trainingState.totalCourses
        })

        // 检查是否是同一个用户和方案
        if (
          trainingState.phone === this.phone &&
          trainingState.planId === this.planId
        ) {
          // 检查保存时间是否在合理范围内（比如24小时内）
          const saveTime = trainingState.saveTime
          const now = Date.now()
          const timeDiff = now - saveTime
          const hoursDiff = timeDiff / (1000 * 60 * 60)

          if (hoursDiff <= 24) {
            // 24小时内的数据才考虑恢复
            this.recoveryTrainingData = trainingState
            return trainingState
          } else {
            console.log('⏰ 保存的训练状态过期（超过24小时），清理数据')
            this.clearSavedTrainingState()
            return null
          }
        } else {
          console.log('👤 保存的训练状态不匹配当前用户或方案，清理数据')
          this.clearSavedTrainingState()
          return null
        }
      } catch (error) {
        console.error('❌ 检查保存的训练状态失败:', error)
        this.clearSavedTrainingState()
        return null
      }
    },

    // 新增：恢复训练状态
    restoreTrainingState (trainingState) {
      try {
        console.log('🔄 开始恢复训练状态...')

        // 恢复基础数据
        this.data = trainingState.data
        this.courses = trainingState.courses.map((course) =>
          this.processCourseUrls(course)
        )

        // 恢复训练进度
        this.trainingStartTime = trainingState.trainingStartTime
        this.trainingDuration = trainingState.trainingDuration
        this.completedCourses = [...trainingState.completedCourses]

        // 恢复运动统计
        this.armCount = trainingState.armCount
        this.legCount = trainingState.legCount

        // 恢复视频播放统计
        this.videoPlayStats = { ...trainingState.videoPlayStats }
        this.currentVideoInfo = { ...trainingState.currentVideoInfo }

        // 恢复当前播放课程
        if (trainingState.playingCourseId) {
          const playingCourse = this.courses.find(
            (course) => course.courseId === trainingState.playingCourseId
          )
          if (playingCourse) {
            this.playingCourse = this.processCourseUrls(playingCourse)
          }
        }

        // 恢复训练状态
        this.isTrainingActive = true
        this.loadingCourse = false

        // 打印恢复前的训练时长
        console.log('📊 恢复前的训练数据:', {
          保存的开始时间: new Date(
            trainingState.trainingStartTime
          ).toLocaleString(),
          保存的训练时长: trainingState.trainingDuration + '秒',
          格式化时长: this.formatDuration(
            trainingState.trainingDuration * 1000
          ),
          当前时长变量: this.trainingDuration + '秒'
        })

        // 重新开始计时（从保存的时间继续）
        this.startTrainingTimer(true)

        // 等待1秒后验证恢复效果
        setTimeout(() => {
          console.log('📊 恢复后1秒的训练数据验证:', {
            当前开始时间: new Date(this.trainingStartTime).toLocaleString(),
            当前训练时长: this.trainingDuration + '秒',
            格式化时长: this.formattedDuration,
            计算验证:
              Math.floor((Date.now() - this.trainingStartTime) / 1000) + '秒'
          })
        }, 1000)

        // 开始自动保存
        this.startAutoSave()

        // 更新进度条
        this.updateProgressBars()

        console.log('✅ 训练状态恢复成功:', {
          训练时长: this.formattedDuration,
          完成课程: this.completedCount + '/' + this.totalCount,
          当前课程: this.playingCourse?.title,
          运动次数: `手臂${this.armCount}次，腿部${this.legCount}次`
        })

        // 恢复训练状态后直接继续训练，不需要再次弹准备窗口
        console.log('🔄 训练状态已恢复，直接继续训练...')

        // 开始姿态检测
        this.startDetection()

        // 继续播放当前视频
        this.$nextTick(() => {
          const video = this.$refs.mainVideo
          if (video && video.play) {
            video.currentTime = 0
            video.play().catch((error) => {
              console.warn('恢复视频播放失败:', error)
            })
          }
        })
      } catch (error) {
        console.error('❌ 恢复训练状态失败:', error)
        this.clearSavedTrainingState()
      }
    },

    // 新增：清理保存的训练状态
    clearSavedTrainingState () {
      try {
        localStorage.removeItem(this.TRAINING_STORAGE_KEY)
        console.log('🗑️ 已清理保存的训练状态')
      } catch (error) {
        console.error('❌ 清理保存的训练状态失败:', error)
      }
    },

    // 新增：显示恢复训练确认对话框
    showRecoveryConfirmDialog () {
      this.showRecoveryDialog = true
    },

    // 新增：确认恢复训练
    confirmRecoveryTraining () {
      this.showRecoveryDialog = false
      if (this.recoveryTrainingData) {
        this.restoreTrainingState(this.recoveryTrainingData)
        this.recoveryTrainingData = null
      }
    },

    // 新增：取消恢复训练
    cancelRecoveryTraining () {
      this.showPre = false
      this.showRecoveryDialog = false
      this.clearSavedTrainingState()
      this.recoveryTrainingData = null
      console.log('❌ 用户取消恢复，清理保存的数据')

      // 开始正常训练
      this.startNormalTraining()
    },

    // 新增：开始正常训练
    startNormalTraining () {
      // 初始化完成后的统计信息
      console.log('🎯 准备开始正常训练，统计信息:', {
        总课程数: this.totalCount,
        已完成数: this.completedCount,
        当前播放课程ID: this.playingCourse?.courseId,
        当前播放课程标题: this.playingCourse?.title,
        已完成课程列表: this.completedCourses
      })

      // 开始自动保存
      this.startAutoSave()

      // 显示准备弹窗，等待用户确认开始
      if (this.showPre) {
        this.showPrepareForExercise()
      }
    },

    // 新增：开始自动保存
    startAutoSave () {
      if (this.autoSaveTimer) {
        clearInterval(this.autoSaveTimer)
      }

      this.autoSaveTimer = setInterval(() => {
        if (this.isTrainingActive && !this.showEndStatsDialog) {
          this.saveTrainingState('自动保存')
        }
      }, this.autoSaveInterval)

      console.log(`⏰ 自动保存已启动，间隔: ${this.autoSaveInterval / 1000}秒`)
    },

    // 新增：停止自动保存
    stopAutoSave () {
      if (this.autoSaveTimer) {
        clearInterval(this.autoSaveTimer)
        this.autoSaveTimer = null
        console.log('⏰ 自动保存已停止')
      }
    },

    // 新增：数据完整性检查
    checkDataIntegrity () {
      if (!this.isTrainingActive) return

      try {
        // 检查关键数据是否完整
        const hasValidData =
          this.planId &&
          this.phone &&
          this.trainingStartTime &&
          this.courses &&
          this.courses.length > 0

        if (!hasValidData) {
          console.warn('⚠️ 数据完整性检查失败，尝试重新加载')
          // 这里可以考虑重新加载数据或提示用户
        } else {
          console.log('✅ 数据完整性检查通过')
        }
      } catch (error) {
        console.error('❌ 数据完整性检查出错:', error)
      }
    },

    // 新增：在关键操作时触发保存
    saveOnCriticalAction (action) {
      console.log(`🎯 关键操作触发保存: ${action}`)
      this.saveTrainingState(`关键操作-${action}`)
    },

    // 新增：播放完成音频
    playCompletedSound () {
      try {
        // 创建音频对象
        const audio = new Audio()

        // 设置音频文件路径
        let audioSrc = ''
        try {
          // 尝试使用require方式加载音频文件
          audioSrc = require('@/h5/doucan/assets/sound_completed.mp3')
        } catch (requireError) {
          console.warn(
            '🔄 require方式加载音频失败，尝试直接路径:',
            requireError
          )
          // 备用方案：使用相对路径（需要把文件放到public目录）
          audioSrc = './static/sound_completed.mp3'
        }

        audio.src = audioSrc

        // 设置音量（0.0 - 1.0）
        audio.volume = 0.8

        console.log('🎵 准备播放完成音频:', audioSrc)

        // 播放音频前先加载
        audio.load()

        // 播放音频
        const playPromise = audio.play()

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('🔊 完成音频播放成功')
            })
            .catch((error) => {
              console.warn('🔇 完成音频播放失败:', error)
              // 音频播放失败时的降级处理
              if (error.name === 'NotAllowedError') {
                console.log(
                  '💡 音频播放被浏览器阻止，可能需要用户交互后才能播放'
                )
              } else if (error.name === 'NotSupportedError') {
                console.log('🚫 浏览器不支持该音频格式')
              }
            })
        }

        // 添加音频事件监听
        audio.addEventListener('loadstart', () => {
          console.log('🎵 音频开始加载')
        })

        audio.addEventListener('canplay', () => {
          console.log('🎵 音频可以播放')
        })

        audio.addEventListener('ended', () => {
          console.log('🎵 完成音频播放结束')
          // 音频播放结束后，启动结束统计视频播放
          this.startEndStatsVideo()
        })

        audio.addEventListener('error', (error) => {
          console.error('🚫 音频加载失败:', error)
          console.error('音频源:', audioSrc)
        })
      } catch (error) {
        console.error('🚫 音频播放异常:', error)
      }
    },

    // 新增：播放动作完成音频
    playActionCompleteSound () {
      try {
        // 创建音频对象
        const audio = new Audio()

        // 设置音频文件路径
        let audioSrc = ''
        try {
          // 尝试使用require方式加载音频文件
          audioSrc = require('@/h5/doucan/assets/sound_completed_1.mp3')
        } catch (requireError) {
          console.warn(
            '🔄 require方式加载动作完成音频失败，尝试直接路径:',
            requireError
          )
          // 备用方案：使用相对路径
          audioSrc = './static/sound_completed_1.mp3'
        }

        audio.src = audioSrc
        audio.volume = 0.8

        console.log('🎵 准备播放动作完成音频:', audioSrc)

        // 播放音频前先加载
        audio.load()

        // 播放音频
        const playPromise = audio.play()

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('🔊 动作完成音频播放成功')
            })
            .catch((error) => {
              console.warn('🔇 动作完成音频播放失败:', error)
            })
        }

        // 添加音频事件监听
        audio.addEventListener('ended', () => {
          console.log('🎵 动作完成音频播放结束')
        })

        audio.addEventListener('error', (error) => {
          console.error('🚫 动作完成音频加载失败:', error)
          console.error('音频源:', audioSrc)
        })
      } catch (error) {
        console.error('🚫 动作完成音频播放异常:', error)
      }
    },

    // 检查是否有下一个视频
    hasNextVideo () {
      if (!this.courses || this.courses.length === 0) {
        return false
      }

      const currentIndex = this.courses.findIndex(
        (course) => course.courseId === this.playingCourse?.courseId
      )

      return currentIndex + 1 < this.courses.length
    },

    // 重新播放当前视频
    replayCurrentVideo () {
      this.$nextTick(() => {
        const video = this.$refs.mainVideo
        if (video) {
          video.currentTime = 0
          video.play().catch((error) => {
            console.warn('重新播放视频失败:', error)
          })
        }
      })
    },

    // 显示动作完成弹窗
    showActionCompleteDialog () {
      // 暂停视频
      const video = this.$refs.mainVideo
      if (video && !video.paused) {
        video.pause()
      }

      // 设置动作名称
      this.currentActionName = this.playingCourse?.title || '当前动作'

      // 获取下一个动作名称
      const currentIndex = this.courses.findIndex(
        (course) => course.courseId === this.playingCourse?.courseId
      )
      const nextCourse = this.courses[currentIndex + 1]
      this.nextActionName = nextCourse?.title || '下一个动作'

      // 播放完成音效
      this.playActionCompleteSound()

      // 显示休息弹窗
      this.showRestDialog = true
      this.startRestCountdown()

      // 启动休息时间数字人视频播放
      this.startRestVideo()
    },

    // 开始休息倒计时
    startRestCountdown () {
      this.restCountdown = 30 // 30秒休息

      this.restTimer = setInterval(() => {
        this.restCountdown--

        if (this.restCountdown <= 0) {
          this.finishRest()
        }
      }, 1000)
    },

    // 结束休息，开始下一个动作
    finishRest () {
      // 清除计时器
      if (this.restTimer) {
        clearInterval(this.restTimer)
        this.restTimer = null
      }

      // 关闭休息弹窗
      this.showRestDialog = false

      // 重置休息视频状态
      this.resetRestVideoState()

      // 切换到下一个视频
      this.playNextVideo()
    },

    // 跳过休息，立即开始下一个动作
    skipRest () {
      console.log('⏭️ 用户选择跳过休息')
      this.finishRest()
    },

    // 重置休息视频状态
    resetRestVideoState () {
      this.restVideoPlayCount = 0
      this.isRestVideoMuted = false // 重置为默认开启声音

      // 停止视频播放
      const video = this.$refs.restVideo
      if (video) {
        video.pause()
        video.currentTime = 0
      }
    },

    // 开始运动（从准备弹窗触发）
    startExerciseFromPrepare () {
      this.showPrepareDialog = false

      // 停止数字人视频播放
      this.stopDigitalHumanVideo()

      // 开始训练计时
      this.startTrainingTimer()

      // 开始姿态检测
      this.startDetection()

      // 开始播放第一个视频
      this.$nextTick(() => {
        const video = this.$refs.mainVideo
        if (video) {
          video.play().catch((error) => {
            console.warn('开始播放视频失败:', error)
          })
        }
      })
    },

    // 停止数字人视频播放
    stopDigitalHumanVideo () {
      const video = this.$refs.digitalHumanVideo
      if (!video) return

      console.log('🛑 停止数字人视频播放')

      // 清除重播定时器（重要：防止定时器继续触发播放）
      if (this.digitalHumanVideoReplayTimer) {
        clearTimeout(this.digitalHumanVideoReplayTimer)
        this.digitalHumanVideoReplayTimer = null
        console.log('✅ 已清除数字人视频重播定时器')
      }

      // 暂停视频
      video.pause()

      // 重置播放状态
      this.isDigitalHumanVideoPlaying = false

      // 可选：重置到开头（如果需要下次重新播放）
      video.currentTime = 0
    },

    // 结束统计视频相关方法
    // 切换结束统计视频静音状态
    toggleEndStatsVideoMute () {
      const video = this.$refs.endStatsVideo
      if (!video) return

      this.isEndStatsVideoMuted = !this.isEndStatsVideoMuted
      video.muted = this.isEndStatsVideoMuted
    },

    // 结束统计视频加载完成
    onEndStatsVideoLoaded () {
      const video = this.$refs.endStatsVideo
      if (!video) return

      console.log('🎬 结束统计视频加载完成')
      // 重置播放次数
      this.endStatsVideoPlayCount = 0

      // 尝试自动播放（默认开启声音）
      // video.play().catch((error) => {
      //   console.log('结束统计视频自动播放失败，尝试静音播放:', error)
      //   // 如果自动播放失败，尝试静音播放
      //   video.muted = true
      //   this.isEndStatsVideoMuted = true
      //   video.play().catch((err) => {
      //     console.error('结束统计视频静音播放也失败:', err)
      //   })
      // })
    },

    // 结束统计视频错误处理
    onEndStatsVideoError (error) {
      console.error('结束统计视频加载错误:', error)
    },

    // 结束统计视频播放结束
    onEndStatsVideoEnded () {
      this.endStatsVideoPlayCount++
      console.log(`🎬 结束统计视频第${this.endStatsVideoPlayCount}次播放完成`)

      if (this.endStatsVideoPlayCount < this.endStatsVideoMaxPlayCount) {
        // 还没达到最大播放次数，自动重播
        console.log(
          `🔄 自动重播结束统计视频 (${this.endStatsVideoPlayCount}/${this.endStatsVideoMaxPlayCount})`
        )
        const video = this.$refs.endStatsVideo
        if (video) {
          video.currentTime = 0
          // 延迟一点时间再开始播放
          setTimeout(() => {
            video.play().catch((error) => {
              console.error('结束统计视频重播失败:', error)
            })
          }, 500) // 500ms延迟
        }
      } else {
        // 达到最大播放次数，停止播放
        console.log(
          `✅ 结束统计视频播放完成 (总共${this.endStatsVideoPlayCount}次)`
        )
      }
    },

    // 启动结束统计视频播放
    startEndStatsVideo () {
      console.log('🎬 准备启动结束统计数字人视频')

      // 延迟一帧确保DOM完全渲染后再启动视频
      this.$nextTick(() => {
        const video = this.$refs.endStatsVideo
        if (!video) {
          console.error('❌ 结束统计视频元素未找到')
          return
        }

        // 重置播放状态
        this.endStatsVideoPlayCount = 0

        // 延迟1秒后开始播放，给弹窗一些展示时间
        setTimeout(() => {
          this.playEndStatsVideo()
        }, 1000)
      })
    },

    // 播放结束统计视频
    playEndStatsVideo () {
      const video = this.$refs.endStatsVideo
      if (!video) return

      console.log('🎬 开始播放结束统计数字人视频')

      // 设置静音状态并尝试播放
      video.muted = this.isEndStatsVideoMuted
      video.play()
        .then(() => {
          console.log('📹 结束统计视频播放成功')
        })
        .catch((error) => {
          console.error('❌ 结束统计视频播放失败:', error)
          // 如果播放失败，尝试静音播放
          if (!video.muted) {
            console.log('🔇 尝试静音播放结束统计视频')
            video.muted = true
            this.isEndStatsVideoMuted = true
            video.play().catch((err) => {
              console.error('❌ 结束统计视频静音播放也失败:', err)
            })
          }
        })
    },

    // 休息时间数字人视频相关方法
    // 切换休息视频静音状态
    toggleRestVideoMute () {
      const video = this.$refs.restVideo
      if (!video) return

      this.isRestVideoMuted = !this.isRestVideoMuted
      video.muted = this.isRestVideoMuted
    },

    // 休息视频加载完成
    onRestVideoLoaded () {
      const video = this.$refs.restVideo
      if (!video) return

      console.log('🎬 休息时间数字人视频加载完成')
      // 重置播放次数
      this.restVideoPlayCount = 0
    },

    // 休息视频错误处理
    onRestVideoError (error) {
      console.error('休息时间数字人视频加载错误:', error)
    },

    // 休息视频播放结束
    onRestVideoEnded () {
      this.restVideoPlayCount++
      console.log(`🎬 休息视频第${this.restVideoPlayCount}次播放完成`)

      if (this.restVideoPlayCount < this.restVideoMaxPlayCount) {
        // 还没达到最大播放次数，自动重播
        console.log(
          `🔄 自动重播休息视频 (${this.restVideoPlayCount}/${this.restVideoMaxPlayCount})`
        )
        const video = this.$refs.restVideo
        if (video) {
          video.currentTime = 0
          // 延迟一点时间再开始播放
          setTimeout(() => {
            video.play().catch((error) => {
              console.error('休息视频重播失败:', error)
            })
          }, 500) // 500ms延迟
        }
      } else {
        // 达到最大播放次数，停止播放
        console.log(
          `✅ 休息视频播放完成 (总共${this.restVideoPlayCount}次)`
        )
      }
    },

    // 启动休息视频播放
    startRestVideo () {
      console.log('🎬 准备启动休息时间数字人视频')

      // 延迟一帧确保DOM完全渲染后再启动视频
      this.$nextTick(() => {
        const video = this.$refs.restVideo
        if (!video) {
          console.error('❌ 休息视频元素未找到')
          return
        }

        // 重置播放状态
        this.restVideoPlayCount = 0

        // 延迟500ms后开始播放，给弹窗一些展示时间
        setTimeout(() => {
          this.playRestVideo()
        }, 500)
      })
    },

    // 播放休息视频
    playRestVideo () {
      const video = this.$refs.restVideo
      if (!video) return

      console.log('🎬 开始播放休息时间数字人视频')

      // 设置静音状态并尝试播放
      video.muted = this.isRestVideoMuted
      video.play()
        .then(() => {
          console.log('📹 休息视频播放成功')
        })
        .catch((error) => {
          console.error('❌ 休息视频播放失败:', error)
          // 如果播放失败，尝试静音播放
          if (!video.muted) {
            console.log('🔇 尝试静音播放休息视频')
            video.muted = true
            this.isRestVideoMuted = true
            video.play().catch((err) => {
              console.error('❌ 休息视频静音播放也失败:', err)
            })
          }
        })
    },

    // 同一动作循环间隔休息相关方法
    // 显示循环间隔休息弹窗
    showLoopRestForContinue () {
      // 暂停视频
      const video = this.$refs.mainVideo
      if (video && !video.paused) {
        video.pause()
      }

      console.log('⏱️ 显示同一动作循环间隔休息弹窗')

      // 显示循环间隔休息弹窗
      this.showLoopRestDialog = true
      this.startLoopRestCountdown()

      // 启动循环间隔休息数字人视频播放
      this.startLoopRestVideo()
    },

    // 开始循环间隔休息倒计时
    startLoopRestCountdown () {
      this.loopRestCountdown = 15 // 15秒休息

      this.loopRestTimer = setInterval(() => {
        this.loopRestCountdown--

        if (this.loopRestCountdown <= 0) {
          this.finishLoopRest()
        }
      }, 1000)
    },

    // 结束循环间隔休息，继续当前动作
    finishLoopRest () {
      // 清除计时器
      if (this.loopRestTimer) {
        clearInterval(this.loopRestTimer)
        this.loopRestTimer = null
      }

      // 关闭循环间隔休息弹窗
      this.showLoopRestDialog = false

      // 重置循环间隔休息视频状态
      this.resetLoopRestVideoState()

      // 重新播放当前视频
      this.replayCurrentVideo()
    },

    // 跳过循环间隔休息，立即继续当前动作
    skipLoopRest () {
      console.log('⏭️ 用户选择跳过循环间隔休息')
      this.finishLoopRest()
    },

    // 重置循环间隔休息视频状态
    resetLoopRestVideoState () {
      this.loopRestVideoPlayCount = 0
      this.isLoopRestVideoMuted = false // 重置为默认开启声音

      // 停止视频播放
      const video = this.$refs.loopRestVideo
      if (video) {
        video.pause()
        video.currentTime = 0
      }
    },

    // 循环间隔休息视频相关方法
    // 切换循环间隔休息视频静音状态
    toggleLoopRestVideoMute () {
      const video = this.$refs.loopRestVideo
      if (!video) return

      this.isLoopRestVideoMuted = !this.isLoopRestVideoMuted
      video.muted = this.isLoopRestVideoMuted
    },

    // 循环间隔休息视频加载完成
    onLoopRestVideoLoaded () {
      const video = this.$refs.loopRestVideo
      if (!video) return

      console.log('🎬 循环间隔休息数字人视频加载完成')
      // 重置播放次数
      this.loopRestVideoPlayCount = 0
    },

    // 循环间隔休息视频错误处理
    onLoopRestVideoError (error) {
      console.error('循环间隔休息数字人视频加载错误:', error)
    },

    // 循环间隔休息视频播放结束
    onLoopRestVideoEnded () {
      this.loopRestVideoPlayCount++
      console.log(`🎬 循环间隔休息视频第${this.loopRestVideoPlayCount}次播放完成`)

      if (this.loopRestVideoPlayCount < this.loopRestVideoMaxPlayCount) {
        // 还没达到最大播放次数，自动重播
        console.log(
          `🔄 自动重播循环间隔休息视频 (${this.loopRestVideoPlayCount}/${this.loopRestVideoMaxPlayCount})`
        )
        const video = this.$refs.loopRestVideo
        if (video) {
          video.currentTime = 0
          // 延迟一点时间再开始播放
          setTimeout(() => {
            video.play().catch((error) => {
              console.error('循环间隔休息视频重播失败:', error)
            })
          }, 500) // 500ms延迟
        }
      } else {
        // 达到最大播放次数，停止播放
        console.log(
          `✅ 循环间隔休息视频播放完成 (总共${this.loopRestVideoPlayCount}次)`
        )
      }
    },

    // 启动循环间隔休息视频播放
    startLoopRestVideo () {
      console.log('🎬 准备启动循环间隔休息数字人视频')

      // 延迟一帧确保DOM完全渲染后再启动视频
      this.$nextTick(() => {
        const video = this.$refs.loopRestVideo
        if (!video) {
          console.error('❌ 循环间隔休息视频元素未找到')
          return
        }

        // 重置播放状态
        this.loopRestVideoPlayCount = 0

        // 延迟500ms后开始播放，给弹窗一些展示时间
        setTimeout(() => {
          this.playLoopRestVideo()
        }, 500)
      })
    },

    // 播放循环间隔休息视频
    playLoopRestVideo () {
      const video = this.$refs.loopRestVideo
      if (!video) return

      console.log('🎬 开始播放循环间隔休息数字人视频')

      // 设置静音状态并尝试播放
      video.muted = this.isLoopRestVideoMuted
      video.play()
        .then(() => {
          console.log('📹 循环间隔休息视频播放成功')
        })
        .catch((error) => {
          console.error('❌ 循环间隔休息视频播放失败:', error)
          // 如果播放失败，尝试静音播放
          if (!video.muted) {
            console.log('🔇 尝试静音播放循环间隔休息视频')
            video.muted = true
            this.isLoopRestVideoMuted = true
            video.play().catch((err) => {
              console.error('❌ 循环间隔休息视频静音播放也失败:', err)
            })
          }
        })
    }
  }
}
</script>

<style scoped>
/* 根布局：全屏自适应，防止溢出 */
.ai-exercise-layout {
  display: flex;
  flex-direction: row;
  width: 100vw;
  height: 100vh;
  min-height: 0;
  min-width: 0;
  box-sizing: border-box;
  background: #232323;
  overflow: hidden;
}

.main-content {
  flex: 3 1 0;
  display: flex;
  flex-direction: column;
  padding: 8px 12px 8px 24px;
  box-sizing: border-box;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

/* 16:9响应式播放器包裹层 */
.video-player-wrapper {
  position: relative;
  width: 100%;
  max-width: 100%;
  flex-shrink: 0;
  background: #181818;
  border-radius: 16px;
  margin-bottom: 18px;
  overflow: hidden;
  /* 16:9高宽比 */
  padding-top: 56.25%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.main-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  background: #111;
  object-fit: cover;
}

.video-player-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  background: #222;
  border-radius: 16px;
}

.video-list-area {
  flex: 1;
  min-height: 0;
  margin-top: 8px;
  background: transparent;
  overflow-x: auto;
  overflow-y: hidden;
  display: flex;
  align-items: stretch;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  cursor: grab;
}

.video-list-area:active {
  cursor: grabbing;
}

.video-list-placeholder {
  width: 100%;
  height: 100%;
  color: #bbb;
  display: flex;
  align-items: center;
  font-size: 16px;
  padding: 12px 0;
  background: #292929;
  border-radius: 8px;
  justify-content: center;
}

.video-playlist {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 12px;
  width: 100%;
  height: 100%;
  align-items: center;
  padding-bottom: 4px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

.video-playlist::-webkit-scrollbar {
  height: 6px;
}

.video-playlist::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.video-playlist::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  transition: background 0.3s ease;
}

.video-playlist::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.video-item {
  flex: 0 0 66.66%;
  min-width: 220px;
  max-width: 340px;
  height: 100%;
  display: flex;
  align-items: stretch;
}

.video-card {
  padding: 14px 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  background: #fff;
  transition: background 0.2s, box-shadow 0.2s;
  cursor: pointer;
  border: 2px solid transparent;
  box-sizing: border-box;
  gap: 12px;
}

.video-card.active,
.video-card:hover {
  background: #e6f7f1;
  border-color: #38b48b;
  box-shadow: 0 4px 16px rgba(53, 223, 164, 0.1);
}

.video-title {
  font-size: 15px;
  font-weight: bold;
  color: #222;
  flex: 1;
  line-height: 1.4;
  word-break: break-word;
  margin: 0;
}

.video-info-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
}

.video-duration {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

.video-status {
  display: flex;
  align-items: center;
}

.training-button {
  background-color: #38b48b;
  color: white;
  border: none;
  padding: 4px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(56, 180, 139, 0.15);
}

.side-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px 24px 24px 12px;
  box-sizing: border-box;
  min-width: 0;
  min-height: 0;
  gap: 4px;
  overflow: auto;
}

.card-area {
  background: #181818;
  border-radius: 14px;
  margin-bottom: 0;
  padding: 14px 12px;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 摄像头区域叠加容器 */
.camera-overlay-container {
  position: relative;
  padding: 0 !important;
  /* 覆盖card-area的padding */
  width: 100%;
  aspect-ratio: 1 / 1;
  /* 1:1正方形比例，高度自适应宽度 */
  overflow: hidden;
}

.camera-placeholder,
.skeleton-placeholder,
.plan-desc-placeholder,
.stats-placeholder {
  color: #aaa;
  font-size: 16px;
  width: 100%;
  text-align: center;
}

/* 训练方案描述内容样式 */
.plan-desc-content {
  width: 100%;
  color: #fff;
  text-align: left;
  padding: 4px;
}

.plan-desc-title {
  font-size: 14px;
  font-weight: bold;
  color: #fff;
  margin-bottom: 8px;
  text-align: center;
}

.plan-desc-requirement {
  font-size: 12px;
  line-height: 1.6;
  word-wrap: break-word;
}

.requirement-label {
  color: #38b48b;
  font-weight: bold;
  margin-bottom: 4px;
  font-size: 12px;
}

.requirement-text {
  color: #ddd;
  text-align: justify;
  word-break: break-word;
}

/* 响应式：移动端上下堆叠 */
@media (max-width: 900px) {
  .ai-exercise-layout {
    flex-direction: column;
    padding: 0;
    height: 100vh;
    min-height: 0;
  }

  .main-content,
  .side-panel {
    padding: 12px 8px;
    min-height: 0;
  }

  .main-content {
    flex: none;
    width: 100%;
    min-width: 0;
    min-height: 0;
  }

  .side-panel {
    flex: none;
    width: 100%;
    flex-direction: row;
    gap: 8px;
    min-width: 0;
    min-height: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }

  .card-area {
    min-width: 0;
    min-height: 0;
    flex: 1;
    margin-bottom: 0;
    padding: 8px;
  }

  .camera-overlay-container {
    aspect-ratio: 1 / 1;
  }

  .video-player-wrapper {
    padding-top: 56.25%;
  }

  .plan-desc-content {
    padding: 4px;
  }

  .plan-desc-title {
    font-size: 14px;
    margin-bottom: 8px;
  }

  .plan-desc-requirement {
    font-size: 12px;
  }

  .requirement-label {
    font-size: 12px;
  }

  /* 移动端统计区域调整 */
  .ai-exercise__stats {
    padding: 6px;
  }

  .stats-title {
    font-size: 14px;
  }

  .indicator-text {
    font-size: 10px;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }

  .stats-card {
    padding: 8px;
    min-height: 70px;
  }

  .stats-icon {
    width: 24px;
    height: 24px;
    font-size: 16px;
  }

  .stats-label {
    font-size: 12px;
  }

  .stats-value {
    font-size: 18px;
  }

  .stats-unit {
    font-size: 10px;
  }

  .stats-separator {
    font-size: 14px;
  }

  .stats-total {
    font-size: 16px;
  }
}

.camera-video,
.camera-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  background: #111;
}

.camera-canvas {
  pointer-events: none;
  background: transparent;
  z-index: 2;
  /* 移除镜像变换，改为在JavaScript中处理坐标镜像 */
}

.camera-video {
  z-index: 1;
  transform: scaleX(-1);
  /* 镜像显示 */
}

/* 运动统计区域 - 全新设计 */
.ai-exercise__stats {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 8px;
  gap: 2px;
}

/* 统计头部 */
.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stats-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  letter-spacing: 0.5px;
}

.stats-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #38b48b;
  animation: pulse 2s infinite;
  transition: background-color 0.3s ease;
}

.indicator-dot.completed {
  background: #ffd700;
  animation: none;
}

.indicator-text {
  font-size: 11px;
  color: #38b48b;
  font-weight: 500;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.6;
  }
}

/* 统计网格布局 */
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  flex: 1;
}

/* 统计卡片 */
.stats-card {
  background: linear-gradient(135deg,
      rgba(255, 255, 255, 0.12) 0%,
      rgba(255, 255, 255, 0.08) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 10px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.stats-card:hover {
  background: linear-gradient(135deg,
      rgba(255, 255, 255, 0.18) 0%,
      rgba(255, 255, 255, 0.12) 100%);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

/* 卡片头部 */
.stats-card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-shrink: 0;
}

.stats-icon {
  font-size: 12px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  flex-shrink: 0;
}

.time-icon {
  background: linear-gradient(135deg, #f093fb, #f5576c);
}

.course-icon {
  background: linear-gradient(135deg, #4facfe, #00f2fe);
}

.arm-icon {
  background: linear-gradient(135deg, #ff6b6b, #ee5a52);
}

.leg-icon {
  background: linear-gradient(135deg, #4ecdc4, #44a08d);
}

.stats-label {
  font-size: 13px;
  color: #fff;
  font-weight: 500;
  opacity: 0.9;
}

/* 数值区域 */
.stats-value-section {
  display: flex;
  align-items: baseline;
  gap: 3px;
  margin-bottom: 6px;
  flex: 1;
  justify-content: center;
}

.stats-value {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

.stats-unit {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 400;
}

.stats-separator {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  margin: 0 2px;
}

.stats-total {
  font-size: 18px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1;
}

/* 进度条 */
.stats-progress {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}

.progress-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.6s ease;
  position: relative;
}

.time-progress {
  background: linear-gradient(90deg, #f093fb, #f5576c);
  width: 0%;
  /* 初始为0，动态更新 */
}

.course-progress {
  background: linear-gradient(90deg, #4facfe, #00f2fe);
  width: 0%;
  /* 初始为0，动态更新 */
}

.arm-progress {
  background: linear-gradient(90deg, #ff6b6b, #ee5a52);
  width: 0%;
  /* 初始为0，动态更新 */
}

.leg-progress {
  background: linear-gradient(90deg, #4ecdc4, #44a08d);
  width: 0%;
  /* 初始为0，动态更新 */
}

.progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.ai-exercise__loading,
.ai-exercise__error,
.ai-exercise__permission {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 90%;
  max-width: 500px;
  color: #fff;
  font-size: 16px;
  text-align: center;
  background: rgba(42, 42, 42, 0.9);
  z-index: 1000;
  padding: 20px;
  border-radius: 15px;
  backdrop-filter: blur(5px);
  box-sizing: border-box;
}

.ai-exercise__error {
  color: #ff6b6b;
}

.permission-desc {
  margin: 10px 0 20px;
  color: #999;
  font-size: 14px;
  line-height: 1.5;
}

.permission-button,
.retry-button {
  margin-top: 20px;
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  background: #4caf50;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.retry-button {
  background: #2196f3;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}

.permission-button:hover,
.retry-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
}

.retry-button:hover {
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.4);
}

/* 结束运动确认弹框 */
.confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  animation: fadeIn 0.3s ease;
}

.confirm-dialog {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  padding: 30px;
  border-radius: 20px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.4s ease;
}

.confirm-title {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
}

.confirm-text {
  color: #666;
  margin-bottom: 30px;
  font-size: 16px;
  line-height: 1.5;
}

.confirm-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.confirm-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;
}

.cancel-btn {
  background: rgba(108, 117, 125, 0.1);
  color: #6c757d;
  border: 1px solid rgba(108, 117, 125, 0.3);
}

.cancel-btn:hover {
  background: rgba(108, 117, 125, 0.2);
  transform: translateY(-2px);
}

.confirm-btn-primary {
  background: linear-gradient(135deg, #ff6b6b, #ee5a52);
  color: white;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

.confirm-btn-primary:hover {
  background: linear-gradient(135deg, #ff5252, #d32f2f);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 107, 107, 0.4);
}

/* 运动结束统计弹框 */
.end-stats-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  animation: fadeIn 0.3s ease;
  padding: 20px;
  box-sizing: border-box;
}

.end-stats-dialog {
  background: linear-gradient(145deg,
      rgba(255, 255, 255, 0.95),
      rgba(255, 255, 255, 0.9));
  backdrop-filter: blur(20px);
  padding: 18px;
  border-radius: 16px;
  width: 90vw;
  height: 85vh;
  overflow-y: auto;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.5s ease;
}

.stats-header-section {
  margin-bottom: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.celebration-icon {
  font-size: 40px;
  margin-bottom: 8px;
  animation: bounce 1s infinite alternate;
}

.end-stats-title {
  font-size: 26px;
  font-weight: 800;
  color: #333;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.encouragement-text {
  color: #555;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 0;
  line-height: 1.4;
}

.end-stats-content {
  margin-bottom: 16px;
}

.stats-summary {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-bottom: 18px;
}

/* 平板和小屏幕适配 */
@media (max-width: 768px) {
  .stats-summary {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}

.summary-card {
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 12px;
  padding: 16px 12px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  text-align: center;
}

.summary-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.summary-icon {
  font-size: 28px;
  margin-bottom: 6px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-summary-icon {
  color: #f093fb;
}

.course-summary-icon {
  color: #4facfe;
}

.arm-summary-icon {
  color: #ff6b6b;
}

.leg-summary-icon {
  color: #4ecdc4;
}

.summary-data {
  color: #333;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #333;
  margin-bottom: 4px;
}

.summary-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.performance-evaluation {
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 12px;
  padding: 16px;
  backdrop-filter: blur(10px);
  text-align: center;
}

.evaluation-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
  padding: 0 !important;
  margin-top: 0 !important;
}

.rating-stars {
  margin-bottom: 8px;
}

.star {
  font-size: 26px;
  margin: 0 2px;
  color: #ddd;
  transition: all 0.3s ease;
}

.star.active {
  color: #ffd700;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
  animation: starGlow 1.5s ease-in-out infinite alternate;
}

.rating-text {
  font-size: 15px;
  color: #555;
  font-weight: 600;
}

.browser-back-tip {
  color: #38b48b !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(56, 180, 139, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(56, 180, 139, 0.3);
}

.end-stats-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.action-btn {
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 100px;
  justify-content: center;
}

.secondary-btn {
  background: rgba(108, 117, 125, 0.1);
  color: #6c757d;
  border: 1px solid rgba(108, 117, 125, 0.3);
}

.secondary-btn:hover {
  background: rgba(108, 117, 125, 0.2);
  transform: translateY(-2px);
}

.primary-btn {
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.primary-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes bounce {
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(-10px);
  }
}

@keyframes starGlow {
  from {
    transform: scale(1);
  }

  to {
    transform: scale(1.1);
  }
}

/* 恢复训练确认弹框样式 */
.recovery-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1002;
  animation: fadeIn 0.3s ease;
}

.recovery-dialog {
  background: linear-gradient(145deg,
      rgba(255, 255, 255, 0.95),
      rgba(255, 255, 255, 0.9));
  backdrop-filter: blur(20px);
  padding: 30px;
  border-radius: 20px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.4s ease;
}

.recovery-header {
  margin-bottom: 20px;
}

.recovery-icon {
  font-size: 48px;
  margin-bottom: 12px;
  animation: rotate 2s linear infinite;
}

.recovery-title {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin: 0;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.recovery-content {
  margin-bottom: 25px;
  text-align: left;
}

.recovery-text {
  color: #555;
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
}

.recovery-details {
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 12px;
  padding: 15px;
  margin: 15px 0;
  backdrop-filter: blur(10px);
}

.recovery-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
}

.recovery-detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #666;
  font-weight: 500;
}

.detail-value {
  color: #333;
  font-weight: 600;
}

.recovery-question {
  color: #555;
  margin: 15px 0 0;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
}

.recovery-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.recovery-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
}

.cancel-recovery-btn {
  background: rgba(108, 117, 125, 0.1);
  color: #6c757d;
  border: 1px solid rgba(108, 117, 125, 0.3);
}

.cancel-recovery-btn:hover {
  background: rgba(108, 117, 125, 0.2);
  transform: translateY(-2px);
}

.confirm-recovery-btn {
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.confirm-recovery-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* 移动端适配 */
@media (max-width: 480px) {

  .confirm-dialog,
  .end-stats-dialog,
  .recovery-dialog {
    margin: 10px;
    padding: 20px;
    max-width: 95%;
  }

  .recovery-buttons {
    flex-direction: column;
    gap: 12px;
  }

  .recovery-btn {
    width: 100%;
  }

  .recovery-icon {
    font-size: 40px;
  }

  .recovery-title {
    font-size: 18px;
  }

  .recovery-text,
  .recovery-question {
    font-size: 14px;
  }

  .recovery-detail-item {
    font-size: 13px;
  }

  .stats-summary {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .end-stats-actions {
    flex-direction: column;
    gap: 12px;
  }

  .action-btn {
    width: 100%;
  }

  .celebration-icon {
    font-size: 40px;
  }

  .end-stats-title {
    font-size: 20px;
  }

  .encouragement-text {
    font-size: 14px;
  }

  .summary-card {
    padding: 12px 10px;
  }

  .summary-icon {
    font-size: 20px;
    height: 28px;
  }

  .summary-value {
    font-size: 18px;
  }

  .summary-label {
    font-size: 11px;
  }
}

/* 结束运动按钮区域 */
.end-exercise-section {
  flex-shrink: 0;
  margin-bottom: 0px;
}

.end-exercise-btn {
  width: 100%;
  padding: 2px 16px;
  background: linear-gradient(135deg, #ff6b6b, #ee5a52);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

.end-exercise-btn:hover {
  background: linear-gradient(135deg, #ff5252, #d32f2f);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 107, 107, 0.4);
}

.end-exercise-btn .btn-icon {
  font-size: 32px;
}

.end-exercise-btn .btn-text {
  font-size: 32px;
}

.btn-icon {
  margin-right: 5px;
}

/* 总运动时长悬浮显示 */
.total-exercise-time-float {
  position: fixed;
  top: 8px;
  left: 8px;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.514);
  backdrop-filter: blur(1px);
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  min-width: 220px;
}

/* 倒计时快结束时的样式（剩余5分钟以下） */
.total-exercise-time-float.countdown-critical {
  background: rgba(255, 243, 205, 0.85);
  border: 1px solid rgba(255, 193, 7, 0.5);
  box-shadow: 0 6px 25px rgba(255, 193, 7, 0.4);
}

.total-exercise-time-float.countdown-critical .time-float-value {
  color: #ff6b35;
  font-weight: 700;
}

/* 倒计时非常紧急时的样式（剩余1分钟以下） */
.total-exercise-time-float.countdown-urgent {
  background: rgba(255, 235, 238, 0.85);
  border: 1px solid rgba(244, 67, 54, 0.5);
  box-shadow: 0 6px 25px rgba(244, 67, 54, 0.4);
  animation: countdown-urgent-pulse 1s ease-in-out infinite alternate;
}

.total-exercise-time-float.countdown-urgent .time-float-value {
  color: #d32f2f;
  font-weight: 800;
}

.total-exercise-time-float.countdown-urgent .time-float-icon {
  animation: countdown-urgent-shake 0.5s ease-in-out infinite alternate;
}

/* 倒计时紧急闪烁动画 */
@keyframes countdown-urgent-pulse {
  0% {
    box-shadow: 0 6px 25px rgba(244, 67, 54, 0.4);
    transform: scale(1);
  }

  100% {
    box-shadow: 0 8px 30px rgba(244, 67, 54, 0.7);
    transform: scale(1.03);
  }
}

/* 倒计时图标震动动画 */
@keyframes countdown-urgent-shake {
  0% {
    transform: rotate(-2deg);
  }

  100% {
    transform: rotate(2deg);
  }
}

.total-exercise-time-float:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
}

.time-float-icon {
  font-size: 36px;
  line-height: 1;
}

.time-float-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.time-float-label {
  font-size: 16px;
  color: #666;
  font-weight: 600;
  line-height: 1;
}

.time-float-value {
  font-size: 32px;
  color: #333;
  font-weight: 700;
  line-height: 1;
}

.time-float-progress {
  font-size: 14px;
  color: #666;
  font-weight: 500;
  line-height: 1.2;
  margin-top: 4px;
}

.test-mode-badge {
  display: inline-block;
  background: #ff4757;
  color: white;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 10px;
  margin-left: 8px;
  font-weight: 700;
  vertical-align: top;
  animation: test-mode-pulse 2s ease-in-out infinite;
}

/* 测试模式标签闪烁动画 */
@keyframes test-mode-pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.6;
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .total-exercise-time-float {
    top: 15px;
    left: 15px;
    padding: 14px 18px;
    gap: 14px;
    min-width: 200px;
  }

  .time-float-icon {
    font-size: 24px;
  }

  .time-float-label {
    font-size: 14px;
  }

  .time-float-value {
    font-size: 20px;
  }

  .time-float-progress {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .total-exercise-time-float {
    top: 10px;
    left: 10px;
    padding: 12px 16px;
    gap: 12px;
    min-width: 180px;
  }

  .time-float-icon {
    font-size: 20px;
  }

  .time-float-label {
    font-size: 12px;
  }

  .time-float-value {
    font-size: 18px;
  }

  .time-float-progress {
    font-size: 10px;
  }
}

/* ==================== 数字人视频准备弹窗样式 ==================== */

/* 平板左右布局容器 */
.prepare-layout {
  display: flex;
  gap: 20px;
  flex: 1;
  height: 100%;
}

/* 左侧视频区域 */
.video-section {
  flex: 2;
  /* 视频占2/3的空间 */
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-container {
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg,
      rgba(255, 255, 255, 0.1) 25%,
      transparent 25%,
      transparent 75%,
      rgba(255, 255, 255, 0.1) 75%),
    linear-gradient(45deg,
      rgba(255, 255, 255, 0.1) 25%,
      transparent 25%,
      transparent 75%,
      rgba(255, 255, 255, 0.1) 75%);
  background-size: 30px 30px;
  background-position: 0 0, 15px 15px;
  opacity: 0.3;
  pointer-events: none;
}

.digital-human-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  background: #000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 1;
}

/* 播放倒计时提示 */
.play-countdown {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 24px 32px;
  border-radius: 16px;
  text-align: center;
  z-index: 2;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  animation: countdown-pulse 1s ease-in-out infinite;
}

.countdown-icon {
  font-size: 36px;
  margin-bottom: 12px;
}

.countdown-text {
  font-size: 16px;
  margin-bottom: 8px;
  font-weight: 500;
}

.countdown-number {
  font-size: 32px;
  font-weight: bold;
  color: #4facfe;
  text-shadow: 0 0 10px rgba(79, 172, 254, 0.5);
}

@keyframes countdown-pulse {

  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
  }

  50% {
    transform: translate(-50%, -50%) scale(1.05);
  }
}

/* 右侧内容区域 */
.content-section {
  flex: 1;
  /* 内容占1/3的空间 */
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  /* 改为hidden避免滚动条影响布局 */
  max-height: 100%;
  min-height: 0;
  /* 确保能够正确收缩 */
}

/* 内容区域标题 */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e9ecef;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.play-progress-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-text {
  font-size: 12px;
  color: #666;
  background: rgba(102, 126, 234, 0.1);
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
}

/* 播报内容文字显示 */
.broadcast-text-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  /* 不允许收缩 */
  overflow-y: auto;
  /* 如果内容过多，允许滚动 */
  max-height: 60%;
  /* 最多占用60%的高度，剩余给按钮 */
}

.broadcast-text {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: white;
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.broadcast-text:last-child {
  margin-bottom: 0;
}

.broadcast-text.highlight {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  transform: translateX(4px);
  box-shadow: 0 3px 10px rgba(102, 126, 234, 0.3);
  border-color: transparent;
}

.broadcast-icon {
  font-size: 18px;
  margin-right: 10px;
  flex-shrink: 0;
}

.broadcast-content {
  font-size: 14px;
  line-height: 1.4;
  font-weight: 500;
}

.broadcast-text.highlight .broadcast-content {
  color: white;
}

/* 准备事项清单增强样式 */
.prepare-checklist {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.prepare-checklist .checklist-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: white;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.prepare-checklist .checklist-item:last-child {
  margin-bottom: 0;
}

.prepare-checklist .checklist-item.completed {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-color: #28a745;
  transform: translateX(4px);
  box-shadow: 0 2px 6px rgba(40, 167, 69, 0.2);
}

.prepare-checklist .checklist-icon {
  font-size: 16px;
  margin-right: 10px;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.prepare-checklist .checklist-item.completed .checklist-icon {
  animation: bounce 0.6s ease-out;
}

@keyframes bounce {

  0%,
  20%,
  53%,
  80%,
  100% {
    transform: translate3d(0, 0, 0);
  }

  40%,
  43% {
    transform: translate3d(0, -6px, 0);
  }

  70% {
    transform: translate3d(0, -3px, 0);
  }

  90% {
    transform: translate3d(0, -1px, 0);
  }
}

.prepare-checklist .checklist-text {
  font-size: 14px;
  line-height: 1.4;
  color: #495057;
  font-weight: 500;
}

.prepare-checklist .checklist-item.completed .checklist-text {
  color: #155724;
  font-weight: 600;
}

/* 准备完成状态的鼓励文字 */
.prepare-encourage {
  text-align: center;
  font-size: 15px;
  line-height: 1.5;
  color: #6c757d;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.4s ease;
  margin-top: 16px;
}

.prepare-encourage.ready {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  font-weight: 600;
  transform: scale(1.02);
  box-shadow: 0 4px 16px rgba(40, 167, 69, 0.3);
  animation: glow 2s ease-in-out infinite alternate;
  border-color: transparent;
}

@keyframes glow {
  from {
    box-shadow: 0 4px 16px rgba(40, 167, 69, 0.3);
  }

  to {
    box-shadow: 0 8px 24px rgba(40, 167, 69, 0.5);
  }
}

/* 开始运动按钮增强样式 */
.start-exercise-btn.ready {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
  animation: pulse 2s ease-in-out infinite;
}

.start-exercise-btn.ready:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4) !important;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }

  100% {
    transform: scale(1);
  }
}

.start-exercise-btn:disabled {
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%) !important;
  cursor: not-allowed !important;
  opacity: 0.7;
  transform: none !important;
  /* 禁用悬停变换效果 */
  box-shadow: 0 2px 8px rgba(108, 117, 125, 0.2) !important;
}

.start-exercise-btn:disabled:hover {
  transform: none !important;
  box-shadow: 0 2px 8px rgba(108, 117, 125, 0.2) !important;
}

/* 开始前准备弹窗样式 */
.prepare-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
}

.prepare-dialog {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 16px;
  max-width: 95vw;
  width: 95%;
  max-height: 95vh;
  height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  animation: prepare-slide-in 0.4s ease-out;
  display: flex;
  flex-direction: column;
  position: relative;
}

.prepare-dialog::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg,
      rgba(255, 255, 255, 0.1) 25%,
      transparent 25%,
      transparent 75%,
      rgba(255, 255, 255, 0.1) 75%),
    linear-gradient(45deg,
      rgba(255, 255, 255, 0.1) 25%,
      transparent 25%,
      transparent 75%,
      rgba(255, 255, 255, 0.1) 75%);
  background-size: 30px 30px;
  background-position: 0 0, 15px 15px;
  opacity: 0.3;
  pointer-events: none;
  border-radius: 20px;
}

@keyframes prepare-slide-in {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.prepare-header {
  text-align: center;
}

.prepare-icon {
  font-size: 48px;
}

.prepare-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.prepare-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}

.prepare-text {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  text-align: center;
}

.prepare-checklist {}

.checklist-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.checklist-item:last-child {
  border-bottom: none;
}

.checklist-icon {
  font-size: 20px;
  margin-right: 15px;
  flex-shrink: 0;
}

.checklist-text {
  font-size: 15px;
  color: #555;
  line-height: 1.4;
}

.prepare-encourage {
  font-size: 16px;
  color: #2196f3;
  font-weight: 500;
  text-align: center;
  margin: 0;
}

.prepare-buttons {
  text-align: center;
  padding-top: 16px;
  flex-shrink: 0;
}

.prepare-btn {
  padding: 15px 40px;
  border: none;
  border-radius: 12px;
  font-size: 32px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  min-width: 180px;
}

/* 按钮容器样式 - 占满剩余空间 */
.prepare-action-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  min-height: 0;
}

.start-exercise-btn {
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
  /* 占满容器宽度和高度 */
  width: 100%;
  flex: 1;
  min-height: 80px;
  border-radius: 16px;
  font-size: 20px;
}

.start-exercise-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

/* 按钮内部元素样式调整 */
.start-exercise-btn .btn-icon {
  font-size: 24px;
}

.start-exercise-btn .btn-text {
  font-size: 20px;
  font-weight: 700;
}

/* 休息倒计时弹窗样式 - 左右布局 */
.rest-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
}

.rest-dialog {
  background: white;
  border-radius: 20px;
  padding: 0;
  width: 80vw;
  height: 80vh;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  animation: rest-slide-in 0.4s ease-out;
  display: flex;
  overflow: hidden;
}

@keyframes rest-slide-in {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 左侧区域：动作完成信息 */
.rest-left-section {
  flex: 1;
  padding: 40px 35px;
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.rest-left-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="20" cy="20" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="80" cy="40" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="40" cy="80" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>') repeat;
  opacity: 0.3;
  pointer-events: none;
}

.rest-header {
  margin-bottom: 25px;
  text-align: center;
  position: relative;
  z-index: 1;
}

.rest-icon {
  font-size: 56px;
  margin-bottom: 15px;
  display: block;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.rest-title {
  font-size: 28px;
  font-weight: 700;
  color: rgb(0, 147, 86);
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.completion-info {
  text-align: center;
  position: relative;
  z-index: 1;
}

.rest-completion-text {
  font-size: 18px;
  color: rgb(0, 147, 86);
  font-weight: 400;
  margin: 0 0 15px 0;
  opacity: 0.9;
}

.completion-sub-text {
  font-size: 18px;
  color: rgb(0, 147, 86);
  font-weight: 400;
  margin: 15px 0 0 0;
  opacity: 0.9;
}

.current-action-badge {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 15px;
  padding: 12px 20px;
  margin: 10px 0;
  font-size: 20px;
  font-weight: 600;
  color: rgb(0, 147, 86);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.next-action-info {
  text-align: center;
  position: relative;
  z-index: 1;
}

.next-action-label {
  font-size: 14px;
  color: rgb(0, 147, 86);
  font-weight: 500;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.next-action-name {
  font-size: 22px;
  color: rgb(0, 147, 86);
  font-weight: 600;
  margin: 0;
}

/* 右侧区域：倒计时和操作 */
.rest-right-section {
  flex: 1;
  padding: 40px 35px;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.rest-countdown-section {
  margin-bottom: 35px;
}

.countdown-circle {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 140px;
  height: 140px;
  border: 5px solid #2196f3;
  border-radius: 50%;
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  margin-bottom: 20px;
  animation: countdown-pulse 2s ease-in-out infinite;
  box-shadow: 0 8px 25px rgba(33, 150, 243, 0.2);
}

@keyframes countdown-pulse {

  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 8px 25px rgba(33, 150, 243, 0.2),
      0 0 0 0 rgba(33, 150, 243, 0.4);
  }

  50% {
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(33, 150, 243, 0.3),
      0 0 0 15px rgba(33, 150, 243, 0);
  }
}

.countdown-number {
  font-size: 42px;
  font-weight: 700;
  color: #1976d2;
  line-height: 1;
}

.countdown-unit {
  font-size: 16px;
  color: #1976d2;
  font-weight: 500;
  margin-top: 2px;
}

.rest-countdown-text {
  font-size: 16px;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

.rest-buttons {
  text-align: center;
}

.rest-btn {
  padding: 15px 35px;
  border: none;
  border-radius: 15px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  min-width: 160px;
}

.skip-rest-btn {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: white;
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.3);
}

.skip-rest-btn:hover {
  background: linear-gradient(135deg, #f57c00, #ef6c00);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(255, 152, 0, 0.4);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .rest-dialog {
    flex-direction: column;
    max-width: 450px;
    min-height: auto;
  }

  .rest-left-section,
  .rest-right-section {
    flex: none;
    padding: 30px 25px;
  }

  .rest-left-section {
    border-radius: 20px 20px 0 0;
  }

  .rest-right-section {
    border-radius: 0 0 20px 20px;
  }

  .rest-title {
    font-size: 22px;
  }

  .rest-icon {
    font-size: 48px;
  }

  .current-action-badge {
    font-size: 18px;
    padding: 10px 16px;
  }

  .next-action-name {
    font-size: 18px;
  }

  .countdown-circle {
    width: 120px;
    height: 120px;
  }

  .countdown-number {
    font-size: 36px;
  }

  .rest-btn {
    padding: 12px 30px;
    font-size: 15px;
    min-width: 140px;
  }
}

@media (max-width: 480px) {
  .rest-dialog {
    margin: 15px;
    width: calc(100% - 30px);
  }

  .rest-left-section,
  .rest-right-section {
    padding: 25px 20px;
  }

  .rest-title {
    font-size: 20px;
  }

  .rest-icon {
    font-size: 42px;
  }

  .current-action-badge {
    font-size: 16px;
    padding: 8px 14px;
  }

  .next-action-name {
    font-size: 16px;
  }

  .countdown-circle {
    width: 100px;
    height: 100px;
  }

  .countdown-number {
    font-size: 28px;
  }

  .countdown-unit {
    font-size: 14px;
  }

  .rest-btn {
    padding: 10px 25px;
    font-size: 14px;
    min-width: 120px;
  }
}

/* 数字人视频音频控制按钮样式 */
.video-controls {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10;
}

.audio-control-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.audio-control-btn:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.audio-control-btn:active {
  transform: translateY(0);
}

.audio-icon {
  font-size: 16px;
  line-height: 1;
}

.audio-text {
  font-size: 12px;
  white-space: nowrap;
}

/* 移动端音频控制按钮适配 */
@media (max-width: 768px) {
  .video-controls {
    top: 10px;
    right: 10px;
  }

  .audio-control-btn {
    padding: 6px 12px;
    font-size: 12px;
  }

  .audio-icon {
    font-size: 14px;
  }

  .audio-text {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .audio-control-btn {
    padding: 5px 10px;
  }

  .audio-text {
    display: none;
    /* 在很小的屏幕上只显示图标 */
  }
}

/* 左右布局主体 */
.end-stats-layout {
  display: flex;
  gap: 20px;
  margin-top: 18px;
  align-items: flex-start;
  height: calc(100% - 100px);
}

/* 左侧视频区域 */
.end-stats-video-section {
  flex: 0 0 67%;
  min-width: 320px;
}

.end-stats-video-container {
  position: relative;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 16/9;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.end-stats-digital-human-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

.end-stats-video-controls {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 10;
}

.end-stats-audio-control-btn {
  background: rgba(0, 0, 0, 0.7);
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  color: white;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 4px;
}

.end-stats-audio-control-btn:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: scale(1.05);
}

/* 右侧数据区域 */
.end-stats-data-section {
  flex: 1;
  text-align: left;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .end-stats-dialog {
    width: 95vw;
    height: 90vh;
    padding: 16px;
  }

  .end-stats-layout {
    flex-direction: column;
    gap: 12px;
    height: calc(100% - 80px);
  }

  .end-stats-video-section {
    flex: none;
    min-width: unset;
  }

  .end-stats-data-section {
    text-align: center;
    flex: 1;
  }

  .stats-summary {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-bottom: 12px;
  }

  .summary-card {
    padding: 10px 8px;
  }

  .summary-icon {
    font-size: 18px;
    height: 24px;
  }

  .summary-value {
    font-size: 16px;
  }

  .summary-label {
    font-size: 11px;
  }
}

/* 休息弹窗中的视频样式 */
/* 左侧视频区域 */
.rest-video-section {
  flex: 0 0 50%;
  min-width: 300px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.rest-video-container {
  position: relative;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 16/9;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  width: 100%;
  height: 100%;
}

.rest-digital-human-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.rest-video-controls {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 10;
}

.rest-audio-control-btn {
  background: rgba(0, 0, 0, 0.7);
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  color: white;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 4px;
}

.rest-audio-control-btn:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: scale(1.05);
}

/* 右侧内容区域 */
.rest-content-section {
  flex: 1;
  padding: 40px 35px;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

}

/* 休息弹窗移动端视频适配 */
@media (max-width: 768px) {
  .rest-dialog {
    flex-direction: column;
    max-width: 450px;
    min-height: auto;
  }

  .rest-video-section {
    flex: none;
    min-width: unset;
    padding: 20px;
    border-radius: 20px 20px 0 0;
  }

  .rest-video-container {
    max-width: none;
  }

  .rest-content-section {
    flex: none;
    padding: 30px 25px;
    border-radius: 0 0 20px 20px;
  }

  .rest-title {
    font-size: 22px;
  }

  .rest-icon {
    font-size: 48px;
  }

  .current-action-badge {
    font-size: 18px;
    padding: 10px 16px;
  }

  .next-action-name {
    font-size: 18px;
  }

  .countdown-circle {
    width: 120px;
    height: 120px;
  }

  .countdown-number {
    font-size: 36px;
  }

  .rest-btn {
    padding: 12px 30px;
    font-size: 15px;
    min-width: 140px;
  }
}

/* 循环间隔进度信息样式 */
.loop-progress-info {
  text-align: center;
  margin: 20px 0;
  background: rgba(0, 147, 86, 0.1);
  border-radius: 12px;
  padding: 15px;
  border: 1px solid rgba(0, 147, 86, 0.2);
}

.progress-label {
  font-size: 14px;
  color: rgb(0, 147, 86);
  font-weight: 500;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.progress-value {
  font-size: 24px;
  color: rgb(0, 147, 86);
  font-weight: 700;
  margin: 0;
}
</style>
